import type { MergeCommitRequest, MergeCommitResponse, MergeStatus } from "./lib/mergeCommit";
import { buildBankNotificationQuery, type BankNotificationQuery, type BankNotifyRuleForm } from "./lib/bankNotifications";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: any;
        ready: () => void;
        expand: () => void;
        close: () => void;
        setHeaderColor: (c: string) => void;
        setBackgroundColor: (c: string) => void;
        themeParams?: any;
        colorScheme?: "light" | "dark";
        viewportHeight: number;
        platform: string;
        version: string;
      };
    };
  }
}

const API_BASE = "https://loyverse-pos-api.daydreamervale.workers.dev";

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    headers["X-Telegram-Init-Data"] = tg.initData;
    return headers;
  }
  const adminSecret = localStorage.getItem("admin_secret");
  if (adminSecret) {
    headers["X-Admin-Secret"] = adminSecret;
    return headers;
  }
  const sessionToken = localStorage.getItem("session_token");
  if (sessionToken) headers["X-Session-Token"] = sessionToken;
  return headers;
}

/** Trả true nếu user đã đăng nhập bằng BẤT KỲ cách nào (Telegram / admin_secret / session_token) */
export function isAuthed(): boolean {
  const tg = window.Telegram?.WebApp;
  if (tg?.initData) return true;
  if (localStorage.getItem("admin_secret")) return true;
  if (localStorage.getItem("session_token")) return true;
  return false;
}

export type AuthMe = { username: string; source: string; can_merge_products?: boolean };

/**
 * Server capability for product merge. Anything but an explicit `true` — including
 * an older server that omits the field — fails closed. A local token is never enough.
 */
export type MergeCapabilityState = { scope: string; allowed: boolean };

export function mergeCapabilityFor(scope: string, me: unknown): MergeCapabilityState {
  const canMerge = (me as { can_merge_products?: unknown } | null)?.can_merge_products;
  return { scope, allowed: canMerge === true };
}

/**
 * Applies only to the identity the answer was fetched for: a stale response or a
 * previous account's capability can never unlock merge for the current scope.
 */
export function mergeCapabilityApplies(state: MergeCapabilityState | null, scope: string): boolean {
  return !!scope && state?.scope === scope && state.allowed === true;
}

/**
 * Latest-request-wins guard: only the newest /auth/me probe for the current
 * account may change the cached answer. A slower, older response (including an
 * older `allowed:true`) is ignored even when it targets the same account.
 */
export function mergeCapabilityResponseCurrent(
  responseSeq: number,
  latestSeq: number,
  responseScope: string,
  currentScope: string,
): boolean {
  return responseSeq === latestSeq && !!currentScope && responseScope === currentScope;
}

export class ApiError extends Error {
  constructor(public status: number, public payload: any) {
    super(payload?.error || `HTTP ${status}`);
  }
}

/* ═══ Bank notifications (read-only) — raw rows captured from banking apps ═══ */
export type BankNotification = {
  id: number;
  notification_id: string | null;
  device_id: string | null;
  app_package: string;
  title: string | null;
  body: string | null;
  amount_vnd: number | null;
  received_at: string | null;
  created_at: string | null;
};
export type BankNotificationListResponse = {
  notifications: BankNotification[];
  limit: number;
  offset: number;
  total?: number;
};

/* ═══ Bank notify amount rules (per app_package) ═══ */
export type BankNotifyRule = {
  app_package: string;
  anchor: string;
  skip_count: number;
  require_sign: boolean;
  speak: boolean;
  updated_at: string | null;
};
export type BankNotifyApp = { app_package: string; count: number; last_seen_at: string | null };
export type BankNotifyRuleListResponse = { rules: BankNotifyRule[]; apps: BankNotifyApp[] };
export type BankNotifyRulePreviewSample = {
  id: number;
  created_at: string | null;
  title: string | null;
  body: string | null;
  amount_vnd: number | null;
  matched_text: string | null;
  reason: string | null;
};
export type BankNotifyRulePreview = {
  samples: BankNotifyRulePreviewSample[];
  parsed_ok: number;
  parsed_null: number;
};
export type BankNotifyRulePreviewParams = {
  app_package?: string;
  anchor?: string;
  skip_count?: number;
  require_sign?: boolean;
  limit?: number;
};
export type BankNotifyRulePatch = Partial<BankNotifyRuleForm>;

/* ═══ Product merge preview (read-only) — mirrors poscloud/worker/src/product-merge.ts ═══ */
export type MergeSaleTotals = { line_count: number; revenue: number; cost: number };
export type MergePurchaseTotals = { line_count: number; lot_count: number };
export type MergeActiveSale = {
  id: number;
  sale_price: number;
  starts_at: string;
  ends_at: string | null;
};
export type MergeStockByStore = { store_id: string; in_stock: number };
export type MergeItemView = {
  item_id: string;
  variant_id: string | null;
  name: string;
  sku: string | null;
  barcode: string | null;
  price: number | null;
  cost: number | null;
  stock_by_store: MergeStockByStore[];
  active_sales: MergeActiveSale[];
};
export type MergeSaleSources = {
  receipts: MergeSaleTotals;
  counter: MergeSaleTotals;
  legacy_sheet: MergeSaleTotals & { included: boolean };
};
export type MergeHistoryMember = {
  item_id: string;
  role: "canonical" | "alias";
  sales: MergeSaleTotals;
  purchases: MergePurchaseTotals;
  sources: MergeSaleSources;
};
export type MergePreview = {
  canonical: MergeItemView;
  aliases: MergeItemView[];
  history: {
    sales: MergeSaleTotals;
    purchases: MergePurchaseTotals;
    by_member: MergeHistoryMember[];
    sources: {
      receipts: MergeSaleTotals;
      counter: MergeSaleTotals;
      legacy_sheet: MergeSaleTotals;
    };
    /** Refund rows whose own receipt link is unknown: their money is NOT safely attributable. */
    reconciliation?: {
      refund_link_ambiguous: boolean;
      ambiguous_refund_lines: number;
    };
  };
  stock: {
    by_store: Array<{
      store_id: string;
      canonical_quantity: number;
      alias_quantity: number;
      combined_quantity: number;
    }>;
    label: string;
  };
  compatible: boolean;
  blockers: Array<{ code: string; message: string }>;
  warnings: string[];
  enforcement_notes?: string[];
  execution_available: boolean;
  /** Added by the route when execution can be requested. */
  store_id?: string | null;
  capability?: { available: boolean; reasons: string[]; schema_present: boolean; vendor_configured: boolean; limitations: string[] };
  execution_token?: { preview_token: string; expires_at: string };
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init.headers || {}) },
  });
  const text = await res.text();
  let payload: any = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}

export type ImportOrderReceiveResult = {
  ok: boolean;
  httpStatus: number;
  incomplete?: boolean;
  finalized?: boolean;
  continue_receive?: boolean;
  remaining_count?: number;
  remaining_new?: string[];
  warnings?: string[];
  added: string[];
  created: string[];
  skipped: string[];
  errors: string[];
  paid_amount_applied?: number | null;
  created_sku?: Array<{ item_name: string; sku: string; item_id: string; qty: number }>;
};

export class ReceiveCheckpointError extends Error {
  constructor(public readonly checkpoint: ImportOrderReceiveResult, cause: unknown) {
    super(cause instanceof Error ? cause.message : "Lỗi nhập kho");
  }
}

// ponytail: continuation chỉ trong lần bấm hiện tại; muốn resume sau reload cần một xác nhận mới.
async function receiveImportOrder(id: number, bookOnly = false): Promise<ImportOrderReceiveResult> {
  const url = `${API_BASE}/api/import-orders/${id}/receive${bookOnly ? "?book_only=1" : ""}`;
  let checkpoint: ImportOrderReceiveResult | null = null;
  let previousRemaining: number | null = null;
  const added = new Set<string>(), created = new Set<string>(), skipped = new Set<string>();
  const errors = new Set<string>(), warnings = new Set<string>();
  const skus = new Map<string, NonNullable<ImportOrderReceiveResult["created_sku"]>[number]>();
  while (true) {
    try {
      // No paid_amount body: preserves the original receive/payment policy on every request.
      const res = await fetch(url, { method: "POST", headers: getAuthHeaders() });
      const text = await res.text();
      let payload: unknown = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
      if (!res.ok) throw new ApiError(res.status, payload);
      if (!payload || typeof payload !== "object" || Array.isArray(payload) || typeof (payload as { ok?: unknown }).ok !== "boolean") {
        throw new Error("Phản hồi nhập kho không hợp lệ");
      }
      const result = { ...payload as ImportOrderReceiveResult, httpStatus: res.status };
      for (const [values, target] of [[result.added, added], [result.created, created], [result.skipped, skipped], [result.errors, errors], [result.warnings, warnings]] as const) {
        if (Array.isArray(values)) for (const value of values) if (typeof value === "string") target.add(value);
      }
      if (Array.isArray(result.created_sku)) for (const sku of result.created_sku) {
        if (sku && typeof sku.item_id === "string" && typeof sku.item_name === "string" && typeof sku.sku === "string" && typeof sku.qty === "number") skus.set(sku.item_id, sku);
      }
      result.added = [...added]; result.created = [...created]; result.skipped = [...skipped];
      result.errors = [...errors]; result.warnings = [...warnings]; result.created_sku = [...skus.values()];
      const remaining = result.remaining_count;
      if (res.status !== 202 || result.continue_receive !== true || result.ok !== false || result.incomplete !== true || result.finalized !== false || result.paid_amount_applied !== null ||
          typeof remaining !== "number" || !Number.isSafeInteger(remaining) || remaining < 0 || (previousRemaining !== null && remaining >= previousRemaining)) return result;
      previousRemaining = remaining;
      checkpoint = result;
    } catch (cause: unknown) {
      if (checkpoint) throw new ReceiveCheckpointError(checkpoint, cause);
      throw cause;
    }
  }
}

export const api = {
  baseUrl: API_BASE,
  authHeaders: getAuthHeaders,
  health: () => request<{ ok: true; time: string; last_loyverse_sync: string | null }>("/api/health"),
  syncStatus: () => request<{ last_loyverse_sync: string | null; recent: SyncLog[] }>("/api/sync/status"),
  triggerSync: () =>
    request<any>("/api/sync/loyverse", { method: "POST" }),
  today: () => request<DashboardToday>("/api/dashboard/today"),
  range: (from: string, to: string) =>
    request<{ from: string; to: string; days: { day: string; orders: number; sales: number }[] }>(
      `/api/dashboard/range?from=${from}&to=${to}`
    ),
  byCategory: (days = 7) =>
    request<{ days: number; categories: CategoryRevenue[] }>(`/api/dashboard/by-category?days=${days}`),
  summary: (from: string, to: string, period?: string) =>
    request<DashboardSummary>(`/api/dashboard/summary?from=${from}&to=${to}${period ? `&period=${period}` : ""}`),
  categoryProducts: (category: string, from: string, to: string) => {
    const key = (category || "").trim() || "Chưa phân loại";
    return request<{ category: string; from: string; to: string; products: CategoryProduct[] }>(
      `/api/dashboard/by-category/${encodeURIComponent(key)}/products?from=${from}&to=${to}`
    );
  },
  byEmployee: (days = 7) =>
    request<{ days: number; employees: EmployeeRevenue[] }>(`/api/dashboard/by-employee?days=${days}`),
  orders: (params: { from?: string; to?: string; limit?: number; offset?: number; payment_type?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.from) sp.set("from", params.from);
    if (params.to) sp.set("to", params.to);
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.offset) sp.set("offset", String(params.offset));
    if (params.payment_type) sp.set("payment_type", params.payment_type);
    return request<{ orders: Order[]; limit: number; offset: number }>(
      `/api/orders?${sp.toString()}`
    );
  },
  order: (id: string) => request<{ order: Order; items: ReceiptItem[] }>(`/api/orders/${id}`),
  products: (params: { q?: string; limit?: number; offset?: number; category_id?: string; include_total?: 0 | 1 } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set("q", params.q);
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.offset) sp.set("offset", String(params.offset));
    if (params.category_id) sp.set("category_id", params.category_id);
    if (params.include_total !== undefined) sp.set("include_total", String(params.include_total));
    return request<{ products: Product[]; limit: number; offset: number }>(
      `/api/products?${sp.toString()}`
    );
  },
  product: (id: string) => request<{ product: Product; variants: Variant[] }>(`/api/products/${id}`),
  /** Read-only duplicate merge preview. POST /api/products/merge/preview (exactly one alias). */
  mergePreview: (canonicalItemId: string, aliasItemId: string) =>
    request<MergePreview>("/api/products/merge/preview", {
      method: "POST",
      body: JSON.stringify({ canonical_item_id: canonicalItemId, alias_item_ids: [aliasItemId] }),
    }),
  /** Guarded commit. Caller owns operation_id + explicit acknowledgements. */
  mergeCommit: (body: MergeCommitRequest) =>
    request<MergeCommitResponse>("/api/products/merge/commit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  /** Status by operation_id — the only allowed follow-up for an unknown outcome. */
  mergeStatus: (operationId: string) =>
    request<MergeStatus>(`/api/products/merge/status/${encodeURIComponent(operationId)}`),
  inventory: (params: { low_stock?: 0 | 1; threshold?: number; limit?: number; virtual_only?: 0 | 1 } = {}) => {
    const sp = new URLSearchParams();
    if (params.low_stock != null) sp.set("low_stock", String(params.low_stock));
    if (params.threshold != null) sp.set("threshold", String(params.threshold));
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.virtual_only != null) sp.set("virtual_only", String(params.virtual_only));
    return request<{ inventory: InventoryRow[] }>(`/api/inventory?${sp.toString()}`);
  },
  /** Lô nhập (stock_lots) của 1 item — FIFO, có remaining + đơn + ngày. */
  itemLots: (itemId: string) =>
    request<{ lots: IMaterialLotExtended[]; consumptions: any[] }>(`/api/stock/materials/${itemId}/lots`),
  virtualRevenueAllocation: {
    capacity: (from: string, to: string) => {
      const sp = new URLSearchParams({ from, to });
      return request<{ capacity: VirtualRevenueCapacity }>(`/api/virtual-revenue-allocation/capacity?${sp.toString()}`);
    },
    preview: (data: VirtualRevenuePreviewRequest) =>
      request<{ preview: VirtualRevenuePreview }>("/api/virtual-revenue-allocation/preview", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    list: (params: VirtualRevenueAllocationFilters = {}) => {
      const sp = new URLSearchParams();
      if (params.id) sp.set("id", params.id);
      if (params.status) sp.set("status", params.status);
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.limit != null) sp.set("limit", String(params.limit));
      if (params.offset != null) sp.set("offset", String(params.offset));
      const qs = sp.toString();
      return request<VirtualRevenueAllocationListResponse>(`/api/virtual-revenue-allocation${qs ? `?${qs}` : ""}`);
    },
    apply: (data: VirtualRevenueApplyRequest) =>
      request<VirtualRevenueApplyResponse>("/api/virtual-revenue-allocation/apply", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    reverse: (id: string, data: VirtualRevenueReverseRequest) =>
      request<VirtualRevenueReverseResponse>(`/api/virtual-revenue-allocation/${encodeURIComponent(id)}/reverse`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  inventoryStats: () =>
    request<InventoryStats>("/api/inventory/stats"),
  inventoryBadge: () =>
    request<Pick<InventoryStats, "out_of_stock" | "low_stock">>("/api/inventory/stats?counts_only=1"),
  categories: {
    list: () =>
      request<{ categories: Category[] }>("/api/categories"),
    create: (data: { name: string; color?: string; has_recipe?: number }) =>
      request<{ category: Category }>("/api/categories", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { name?: string; color?: string; has_recipe?: number }) =>
      request<{ category: Category }>(`/api/categories/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/categories/${id}`, { method: "DELETE" }),
    items: (id: string) =>
      request<CategoryItemsResponse>(`/api/categories/${id}/items`),
  },
  login: (username: string, password: string) =>
    request<{ ok: boolean; token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }).then((r) => {
      localStorage.setItem("session_token", r.token);
      return r;
    }),
  logout: () => {
    localStorage.removeItem("session_token");
    localStorage.removeItem("admin_secret");
    return Promise.resolve();
  },
  me: () => request<AuthMe>("/auth/me"),
  salesHistory: (params: { from: string; to: string; q?: string; category?: string; limit?: number; offset?: number }) => {
    const sp = new URLSearchParams();
    sp.set("from", params.from);
    sp.set("to", params.to);
    if (params.q) sp.set("q", params.q);
    if (params.category) sp.set("category", params.category);
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.offset) sp.set("offset", String(params.offset));
    return request<{ rows: SalesHistoryRow[]; total: number; limit: number; offset: number }>(`/api/sales-history?${sp.toString()}`);
  },
  salesHistoryStats: (from: string, to: string, q?: string, category?: string) => {
    const sp = new URLSearchParams();
    sp.set("from", from);
    sp.set("to", to);
    if (q) sp.set("q", q);
    if (category) sp.set("category", category);
    return request<SalesHistoryStats>(`/api/sales-history/stats?${sp.toString()}`);
  },
  salesHistoryCategories: () =>
    request<{ categories: string[] }>("/api/sales-history/categories"),

  // ── Web POS ────────────────────────────────────────
  pos: {
    catalog: (params: PosCatalogParams = {}) => {
      const sp = new URLSearchParams();
      if (params.limit != null) sp.set("limit", String(params.limit));
      if (params.offset != null) sp.set("offset", String(params.offset));
      if (params.updated_since) sp.set("updated_since", params.updated_since);
      const qs = sp.toString();
      return request<PosCatalogResponse>(`/api/pos/catalog${qs ? `?${qs}` : ""}`);
    },
    barcode: (code: string) =>
      request<PosBarcodeResponse>(`/api/pos/products/by-barcode?${new URLSearchParams({ code })}`),
    getCart: () => request<PosGetCartResponse>("/api/pos/cart"),
    putCart: async (body: PosPutCartRequest): Promise<PosPutCartResponse> => {
      try {
        return await request<PosPutCartResponse>("/api/pos/cart", { method: "PUT", body: JSON.stringify(body) });
      } catch (error: unknown) {
        if (error instanceof ApiError && error.status === 409 && error.payload && typeof error.payload === "object") return error.payload as PosPutCartResponse;
        throw error;
      }
    },
    getWsticket: () => request<PosWsTicketResponse>("/api/pos/realtime/wsticket"),
    webSales: (body: WebPosSaleRequest) =>
      request<PosSaleResponse>("/api/pos/web-sales", { method: "POST", body: JSON.stringify(body) }),
    sale: (clientSaleId: string) =>
      request<PosSaleDetailResponse>(`/api/pos/sales/${encodeURIComponent(clientSaleId)}`),
    sales: (params: PosSalesParams = {}) => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.device_id) sp.set("device_id", params.device_id);
      if (params.include_lines) sp.set("include_lines", "1");
      if (params.limit != null) sp.set("limit", String(params.limit));
      return request<PosSalesResponse>(`/api/pos/sales?${sp.toString()}`);
    },
    voidSale: (clientSaleId: string, body: PosVoidRequest) =>
      request<PosVoidResponse>(`/api/pos/sales/${encodeURIComponent(clientSaleId)}/void`, {
        method: "POST", body: JSON.stringify(body),
      }),
    returnSale: (clientSaleId: string, body: PosReturnRequest) =>
      request<PosReturnResponse>(`/api/pos/sales/${encodeURIComponent(clientSaleId)}/return`, {
        method: "POST", body: JSON.stringify(body),
      }),
    drawerBalance: () => {
      return request<PosDrawerBalanceResponse>(`/api/pos/drawer-balance`);
    },
    // ── PIN / unlock window (SEAM — contract chờ worker chốt) ──
    // PIN chỉ là hàng rào hạn chế (deterrent), KHÔNG phải kiểm soát quyền.
    getSettings: () => request<PosPinSettings>(`/api/pos/settings`),
    /** Đặt PIN mới KHÔNG cần PIN cũ — user dashboard đã đăng nhập = đường khôi phục. */
    setPin: (pin: string) =>
      request<{ ok: boolean; pin_set: boolean }>(`/api/pos/pin`, {
        method: "POST",
        body: JSON.stringify({ pin }),
      }),
    updateSettings: (data: { unlock_window_minutes: number }) =>
      request<{ ok: boolean; unlock_window_minutes: number }>(`/api/pos/settings`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    printRequest: (clientSaleId: string, body: PosPrintRequest) =>
      request<PosPrintRequestResponse>(`/api/pos/sales/${encodeURIComponent(clientSaleId)}/print-requests`, { method: "POST", body: JSON.stringify(body) }),
  },

  // ── Bank notifications (read-only) ────────────────
  bankNotifications: {
    list: (params: BankNotificationQuery = {}) => {
      const qs = buildBankNotificationQuery(params);
      return request<BankNotificationListResponse>(
        `/api/pos/bank-notifications${qs ? `?${qs}` : ""}`
      );
    },
  },

  // ── Bank notify amount rules (per app_package) ────
  bankNotifyRules: {
    list: () => request<BankNotifyRuleListResponse>(`/api/bank-notify-rules`),
    save: (appPackage: string, patch: BankNotifyRulePatch) =>
      request<BankNotifyRule>(`/api/bank-notify-rules/${encodeURIComponent(appPackage)}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      }),
    remove: (appPackage: string) =>
      request<{ ok: boolean }>(`/api/bank-notify-rules/${encodeURIComponent(appPackage)}`, {
        method: "DELETE",
      }),
    preview: (params: BankNotifyRulePreviewParams) =>
      request<BankNotifyRulePreview>(`/api/bank-notify-rules/preview`, {
        method: "POST",
        body: JSON.stringify(params),
      }),
  },

  // ── Tax (P1-5) ────────────────────────────────────
  tax: {
    eligibility: (year: number) =>
      request<TaxEligibility>(`/api/tax/eligibility?year=${year}`),
    report: (year: number, quarter: number) =>
      request<TaxReport>(`/api/tax/report?year=${year}&quarter=${quarter}`),
    createFiling: (data: { year: number; quarter: number }) =>
      request<{ filing: TaxFiling }>("/api/tax/filings", { method: "POST", body: JSON.stringify(data) }),
    lockPeriod: (data: { year: number; quarter: number }) => {
      const startMonth = (data.quarter - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      const endDate = new Date(data.year, endMonth, 0);
      const period_start = `${data.year}-${String(startMonth).padStart(2, "0")}-01`;
      const period_end = `${data.year}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")}`;
      return request<{ lock: Record<string, unknown> }>("/api/tax/audit-lock", {
        method: "POST",
        body: JSON.stringify({ table_name: "tax_filings", period_start, period_end, note: `Khóa Q${data.quarter}/${data.year}` }),
      });
    },
  },

  // ── Items CRUD (P1-9) ─────────────────────────────
  items: {
    bulkUpdate: (body: BulkItemUpdateRequest) =>
      request<BulkOperationResult>("/api/items/bulk-update", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    create: (data: ItemCreate) =>
      request<{ item: any; variants: any[] }>("/api/items", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: ItemUpdate) =>
      request<{ item: any; variants: any[] }>(`/api/items/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/items/${id}`, { method: "DELETE" }),
    detail: (id: string) =>
      request<ItemDetail>(`/api/items/${id}/detail`),
    history: (id: string) =>
      request<ItemHistory>(`/api/items/${id}/history`),
    search: (query: string) =>
      request<{ items: SearchItem[] }>(`/api/items/search?q=${encodeURIComponent(query)}`),
    // Lịch sử sửa hàng loạt (items_audit_log) cho shop owner — xem ItemAuditHistory.
    auditLog: (params: ItemAuditLogQuery = {}) => {
      const sp = new URLSearchParams();
      if (params.q) sp.set("q", params.q);
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.action) sp.set("action", params.action);
      if (params.limit != null) sp.set("limit", String(params.limit));
      const qs = sp.toString();
      return request<ItemAuditLogResponse>(`/api/items/audit-log${qs ? `?${qs}` : ""}`);
    },
    bulkImport: (md: string) => {
      // Parse markdown table → items array
      // Format: | Tên SP | SKU | Giá bán | Giá vốn |
      const lines = md.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.startsWith("|") && l.endsWith("|"));
      // Skip header + separator rows (rows with only --- |)
      const items = lines
        .filter((l) => !/^\|[\s\-:|]+\|$/.test(l))
        .map((l) => l.split("|").slice(1, -1).map((c) => c.trim()))
        .filter((cells, idx) => idx > 0 || (cells[0] && !/tên\s*sp|name|item/i.test(cells[0])))
        .map((cells) => ({
          item_name: cells[0] || "",
          sku: cells[1] || undefined,
          default_price: Number(cells[2]) || 0,
          cost: Number(cells[3]) || 0,
        }))
        .filter((i) => i.item_name);
      return request<BulkImportResult>("/api/items/bulk-import", { method: "POST", body: JSON.stringify({ items }) });
    },
  },

  // ── Product SALE promo (not receipt sales) ───────
  productSales: {
    list: (status: "active" | "all" = "active", q?: string) => {
      const sp = new URLSearchParams({ status });
      if (q?.trim()) sp.set("q", q.trim());
      return request<{ sales: ProductSale[] }>(`/api/product-sales?${sp.toString()}`);
    },
    create: (body: {
      variant_id: string;
      sale_price: number;
      starts_at?: string | null;
      ends_at?: string | null;
      note?: string;
    }) =>
      request<{ sale: ProductSale; lv_patched?: number; lv_error?: string | null }>(
        "/api/product-sales",
        { method: "POST", body: JSON.stringify(body) },
      ),
    end: (id: number) =>
      request<{ sale: ProductSale; idempotent?: boolean; warning?: string | null }>(
        `/api/product-sales/${id}/end`,
        { method: "POST" },
      ),
    update: (id: number, patch: { sale_price?: number; ends_at?: string | null }) =>
      request<{ ok: boolean; ended?: boolean; warning?: string | null; sale: ProductSale }>(
        `/api/product-sales/${id}`,
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
    bulk: (body: BulkProductSaleRequest) =>
      request<BulkOperationResult>("/api/product-sales/bulk", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ── Inventory adjust ─────────────────────────────
  inventoryAdjust: (data: { item_id: string; variant_id: string; delta: number; reason?: string }) =>
    request<InventoryAdjustResult>("/api/inventory/adjust", { method: "POST", body: JSON.stringify(data) }),

  // ── Product Images (P2-12) ──────────────────────────
  productImages: {
    list: (identifier: string) =>
      request<{ images: ProductImage[] }>(`/api/products/${encodeURIComponent(identifier)}/images`),
    imageUrl: (imageId: string) => `${API_BASE}/api/images/${imageId}`,
    delete: (imageId: string) =>
      request<{ ok: boolean }>(`/api/images/${imageId}`, { method: "DELETE" }),
    loadBlob: async (imageId: string): Promise<string> => {
      const r = await fetch(`${API_BASE}/api/images/${imageId}`, { headers: getAuthHeaders() });
      const blob = await r.blob();
      return URL.createObjectURL(blob);
    },
    upload: async (productId: string, file: File): Promise<{ ok: boolean; image_id?: string; url?: string; error?: string }> => {
      const form = new FormData();
      form.append("image", file);
      const r = await fetch(`${API_BASE}/api/products/${encodeURIComponent(productId)}/image`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: form,
      });
      return r.json();
    },
  },

  // ── Expenses (P0-6) ───────────────────────────────
  expenses: {
    list: (params: { from?: string; to?: string; category?: string; limit?: number; offset?: number } = {}) => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.category) sp.set("category", params.category);
      if (params.limit) sp.set("limit", String(params.limit));
      if (params.offset) sp.set("offset", String(params.offset));
      return request<{ expenses: Expense[] }>(`/api/expenses?${sp.toString()}`);
    },
    create: (data: { expense_date: string; category: string; amount: number; description?: string; attachment_url?: string; source?: string }) =>
      request<{ expense: Expense }>("/api/expenses", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Expense>) =>
      request<{ expense: Expense }>(`/api/expenses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<{ ok: boolean }>(`/api/expenses/${id}`, { method: "DELETE" }),
  },

  // ── Payables (P1-11) ──────────────────────────────
  payables: {
    list: (status?: string) => {
      const sp = new URLSearchParams();
      if (status) sp.set("status", status);
      return request<{ payables: Payable[]; total: number }>(`/api/payables?${sp.toString()}`);
    },
    create: (data: Omit<Payable, "id" | "remaining_amount" | "status" | "created_at" | "updated_at">) =>
      request<{ ok: boolean; id: number }>("/api/payables", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Payable>) =>
      request<{ ok: boolean }>(`/api/payables/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    recordPayment: (id: number, payment: { amount: number; payment_method?: string; note?: string; attachment_url?: string }) =>
      request<{ payable: Payable }>(`/api/payables/${id}/payment`, { method: "POST", body: JSON.stringify(payment) }),
  },

  // ── Purchase ledger / VAT invoices (P1-5) ─────────
  purchaseLedger: {
    list: (params: { from?: string; to?: string; limit?: number } = {}) => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.limit) sp.set("limit", String(params.limit));
      return request<{ entries: PurchaseLedgerEntry[]; total: number }>(`/api/purchase-ledger?${sp.toString()}`);
    },
    create: (data: Omit<PurchaseLedgerEntry, "id" | "created_at">) =>
      request<{ ok: boolean; id: number }>("/api/purchase-ledger", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Record<string, any>) =>
      request<{ ok: boolean }>(`/api/purchase-ledger/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  },

  vatInvoices: {
    list: () =>
      request<{ invoices: VatInvoice[] }>("/api/vat-invoices"),
    create: (data: Omit<VatInvoice, "id" | "created_at">) =>
      request<{ ok: boolean; id: number }>("/api/vat-invoices", { method: "POST", body: JSON.stringify(data) }),
  },

  // ── Import orders (P1-6) ──────────────────────────
  importOrders: {
    list: (params?: { status?: string; from?: string; to?: string; q?: string; limit?: number }) => {
      const sp = new URLSearchParams();
      if (params?.status) sp.set("status", params.status);
      if (params?.from) sp.set("from", params.from);
      if (params?.to) sp.set("to", params.to);
      if (params?.q) sp.set("q", params.q);
      if (params?.limit) sp.set("limit", String(params.limit));
      const qs = sp.toString();
      return request<{ orders: ImportOrder[] }>(`/api/import-orders${qs ? "?" + qs : ""}`);
    },
    /** Full-dataset aggregates (imported only) — NOT derived from the capped list. */
    analytics: (params: { from?: string; to?: string; signal?: AbortSignal } = {}) => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      const qs = sp.toString();
      return request<ImportOrderAnalytics>(`/api/import-orders/analytics${qs ? "?" + qs : ""}`, {
        signal: params.signal,
      });
    },
    create: (data: Omit<ImportOrder, "id" | "items" | "created_at" | "updated_at" | "status"> & { items: Omit<ImportOrderItem, "id">[] }) =>
      request<{ ok: boolean; id: number }>("/api/import-orders", { method: "POST", body: JSON.stringify(data) }),
    // items[] có thể mang id/line_id (import_order_items.id) khi edit — backend dùng detect unmap/remap sau rename
    update: (id: number, data: Partial<ImportOrder> & { client_request_id?: string; items?: Array<Omit<ImportOrderItem, "id"> & { id?: number | null; line_id?: number | null }> }) =>
      request<{ ok: boolean; created_sku?: Array<{ item_name: string; sku: string; item_id: string; qty: number }>; created_count?: number; create_errors?: string[]; stock_lot_warnings?: string[] }>(`/api/import-orders/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id: number) =>
      request<{ ok: boolean; deleted_id: number; s1a_regen_dates: string[] }>(`/api/import-orders/${id}`, { method: "DELETE" }),
    // Không gửi paid_amount: mặc định backend xử lý theo chính sách receive (chỉ trả đủ khi đơn pending lần đầu & paid hiện tại = 0).
    receive: receiveImportOrder,
    attachVat: (id: number, data: { invoice_no?: string; vat_rate?: number }) =>
      request<{ ok: boolean; invoice_no: string | null; vat_rate: number }>(`/api/import-orders/${id}/attach-vat`, { method: "POST", body: JSON.stringify(data) }),
    scanInvoice: async (file: File, extraPrompt?: string): Promise<{ ok: boolean; scanned: ScannedInvoice; provider: string; model: string }> => {
      const fd = new FormData();
      fd.append("image", file);
      if (extraPrompt && extraPrompt.trim()) fd.append("extra_prompt", extraPrompt.trim());
      // Bỏ Content-Type để browser tự set multipart boundary
      const headers = getAuthHeaders();
      delete headers["Content-Type"];
      const r = await fetch(`${API_BASE}/api/import-orders/scan-invoice`, { method: "POST", headers, body: fd });
      const data = await r.json();
      if (!r.ok) throw new ApiError(r.status, data);
      return data;
    },
    aiEditForm: (body: { instruction: string; form: any }) =>
      request<{ ok: boolean; summary: string; form: any; provider?: string; model?: string }>(
        "/api/import-orders/ai-edit-form",
        { method: "POST", body: JSON.stringify(body) }
      ),
    /** Chat scoped to import-order form — messages + form snapshot + optional files. */
    aiChat: async (body: {
      messages: Array<{ role: string; content: string }>;
      form: any;
      order_id?: number | null;
      files?: Array<{ base64: string; mime_type: string; name: string }>;
    }): Promise<{
      assistant_text: string;
      form_patch?: any | null;
      pendingAction?: AgentPendingAction | null;
      tool_trace?: string[];
      provider?: string;
      model?: string;
    }> => {
      const files = body.files || [];
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("messages", JSON.stringify(body.messages));
        fd.append("form", JSON.stringify(body.form ?? {}));
        if (body.order_id != null && body.order_id !== undefined) {
          fd.append("order_id", String(body.order_id));
        }
        for (const f of files) {
          const bin = atob(f.base64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          fd.append("files", new Blob([arr], { type: f.mime_type || "application/octet-stream" }), f.name || "file");
        }
        const headers = getAuthHeaders();
        delete headers["Content-Type"];
        const r = await fetch(`${API_BASE}/api/import-orders/ai-chat`, { method: "POST", headers, body: fd });
        const data = await r.json();
        if (!r.ok) throw new ApiError(r.status, data);
        return data;
      }
      return request("/api/import-orders/ai-chat", {
        method: "POST",
        body: JSON.stringify({
          messages: body.messages,
          form: body.form,
          order_id: body.order_id ?? null,
        }),
      });
    },
    uploadInvoice: async (id: number, file: File): Promise<{ ok: boolean; invoice_file_id: string; invoice_file_type: string }> => {
      const fd = new FormData();
      fd.append("invoice", file);
      const headers = getAuthHeaders();
      delete headers["Content-Type"];
      const r = await fetch(`${API_BASE}/api/import-orders/${id}/invoice`, { method: "POST", headers, body: fd });
      const data = await r.json();
      if (!r.ok) throw new ApiError(r.status, data);
      return data;
    },
    invoiceUrl: (id: number) => `${API_BASE}/api/import-orders/${id}/invoice`,
    vatBackfill: (id: number, data: { mapped_item_id: string; import_order_item_id?: number | null; sub_type?: "vat" | "bangke"; purchased_qty: number; preview?: boolean; force_locked?: boolean }) =>
      request<VatBackfillResult>(`/api/import-orders/${id}/vat-backfill`, { method: "POST", body: JSON.stringify(data) }),
    vatBackfillRemove: (id: number, data: { mapped_item_id: string; sub_type?: "vat" | "bangke"; scope?: "order" | "all"; force_locked?: boolean }) =>
      request<{ removed_count: number; removed_qty: number; affected_dates: string[] }>(`/api/import-orders/${id}/vat-backfill`, { method: "DELETE", body: JSON.stringify(data) }),
    // Chứng từ đính kèm (Google Drive)
    attachments: {
      list: (orderId: number) =>
        request<{ attachments: ImportOrderAttachment[] }>(`/api/import-orders/${orderId}/attachments`),
      upload: (orderId: number, data: { file_name: string; base64: string; mime_type: string }) =>
        request<{ ok: true; attachment: ImportOrderAttachment }>(`/api/import-orders/${orderId}/attachments`, { method: "POST", body: JSON.stringify(data) }),
      remove: (orderId: number, attId: string) =>
        request<{ ok: true }>(`/api/import-orders/${orderId}/attachments/${attId}`, { method: "DELETE" }),
    },
    // Dò đơn nhập khớp NCC/tổng tiền (để gắn chứng từ vào đơn cũ)
    match: (data: { supplier_name?: string; total_amount?: number; days?: number }) =>
      request<{ candidates: Array<{ id: number; supplier_name: string | null; total_amount: number; order_date: string | null; item_count: number }> }>(`/api/import-orders/match`, { method: "POST", body: JSON.stringify(data) }),
    // Tách 1 dòng SP thành N phần (sum qty = qty gốc; 409 nếu đã có stock_lots)
    splitLine: (orderId: number, lineId: number, parts: Array<{ quantity: number; item_name?: string; mapped_item_id?: string }>) =>
      request<{ ok: true; created: number }>(`/api/import-orders/${orderId}/items/${lineId}/split`, { method: "POST", body: JSON.stringify({ parts }) }),
  },

  // ── Sổ S1A-HKD + thông tin hộ kinh doanh ─────────
  s1a: {
    exportUrl: (year: number, quarter?: number) => {
      const sp = new URLSearchParams();
      sp.set("year", String(year));
      if (quarter) sp.set("quarter", String(quarter));
      return `${API_BASE}/api/s1a-export?${sp.toString()}`;
    },
    download: async (year: number, quarter?: number): Promise<Blob> => {
      const sp = new URLSearchParams();
      sp.set("year", String(year));
      if (quarter) sp.set("quarter", String(quarter));
      const headers = getAuthHeaders();
      const r = await fetch(`${API_BASE}/api/s1a-export?${sp.toString()}`, { headers });
      if (!r.ok) throw new ApiError(r.status, await r.json().catch(() => ({})));
      return r.blob();
    },
    generateRange: async (from: string, to: string): Promise<{ ok: boolean; days_processed: number; total_inserted: number; total_revenue: number; message?: string }> => {
      const r = await fetch(`${API_BASE}/api/s1a-generate-range?from=${from}&to=${to}`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new ApiError(r.status, await r.json().catch(() => ({})));
      return r.json();
    },
    generateDate: async (date: string): Promise<{
      date: string;
      skipped?: string;
      total_receipts: number;
      vat_orders: number;
      bangke_orders: number;
      vat_items: number;
      bangke_items: number;
      total_revenue: number;
    }> => {
      const r = await fetch(`${API_BASE}/api/s1a/generate-date?date=${date}`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new ApiError(r.status, await r.json().catch(() => ({})));
      return r.json();
    },
  },
  hkdInfo: {
    get: () => request<HkdInfo>("/api/hkd-info"),
    update: (data: Partial<HkdInfo>) =>
      request<{ ok: boolean }>("/api/hkd-info", { method: "PUT", body: JSON.stringify(data) }),
  },
  virtualOrders: {
    list: (params: { from?: string; to?: string; type?: string; limit?: number } = {}) => {
      const sp = new URLSearchParams();
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      if (params.type) sp.set("type", params.type);
      if (params.limit) sp.set("limit", String(params.limit));
      return request<{ virtual_orders: VirtualOrder[] }>(`/api/virtual-orders?${sp.toString()}`);
    },
  },

  virtualInventory: {
    list: () => request<{ items: VirtualInventoryItem[] }>(`/api/virtual-inventory`),
    create: (data: { item_name: string; sku?: string; qty?: number; unit_cost?: number; store_id?: string; note?: string }) =>
      request<{ ok: boolean; id: number }>(`/api/virtual-inventory`, { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: Partial<{ item_name: string; sku: string; qty: number; unit_cost: number; store_id: string; note: string }>) =>
      request<{ ok: boolean }>(`/api/virtual-inventory/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id: number) => request<{ ok: boolean }>(`/api/virtual-inventory/${id}`, { method: "DELETE" }),
  },

  s1aEntries: {
    list: (year: number, quarter?: number) => {
      const sp = new URLSearchParams();
      sp.set("year", String(year));
      if (quarter) sp.set("quarter", String(quarter));
      return request<{ entries: S1aEntry[] }>(`/api/s1a-entries?${sp.toString()}`);
    },
  },

  // ── Stock lots / alert (P1-3) ─────────────────────
  stockLots: {
    list: (itemId?: string) => {
      const sp = new URLSearchParams();
      if (itemId) sp.set("item_id", itemId);
      return request<{ lots: StockLot[]; total: number }>(`/api/stock-lots?${sp.toString()}`);
    },
    create: (data: Omit<StockLot, "id" | "created_at">) =>
      request<{ ok: boolean; id: number }>("/api/stock-lots", { method: "POST", body: JSON.stringify(data) }),
  },

  stockAlert: {
    list: () =>
      request<StockAlertList>("/api/stock-alert"),
    update: (itemId: string, settings: { threshold: number; enabled: boolean }) =>
      request<{ ok: boolean }>(`/api/stock-alert/${itemId}`, { method: "PATCH", body: JSON.stringify(settings) }),
  },

  // ── Stock take (P2-4) ─────────────────────────────
  stockTake: {
    sessions: () =>
      request<{ sessions: StockTakeSession[] }>("/api/stock-take/sessions"),
    create: (data: { session_date?: string; notes?: string }) =>
      request<{ session: StockTakeSession }>("/api/stock-take/sessions", { method: "POST", body: JSON.stringify(data) }),
    updateItems: (sessionId: number, items: { item_id: string; variant_id?: string; actual_qty: number; reason?: string }[]) =>
      request<{ updated: number; total: number; diff_count: number }>(`/api/stock-take/sessions/${sessionId}/items`, { method: "PATCH", body: JSON.stringify({ items }) }),
    complete: (sessionId: number) =>
      request<{ session: StockTakeSession; adjusted: number }>(`/api/stock-take/sessions/${sessionId}/complete`, { method: "POST" }),
  },

  // ── Barcode printing (P2-3) ──────────────────────
  barcode: {
    generate: (items: Array<{ sku: string; name: string; price: number; qty?: number }>) =>
      fetch(`${API_BASE}/api/barcode/generate`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: JSON.stringify({ items }),
      }).then((r) => r.text()),
  },

  // ── Dashboard comparison (P2-6) ───────────────────
  dashboardComparison: (period: "week" | "month") =>
    request<ComparisonData>(`/api/dashboard/comparison?period=${period}`),

  // ── Products list helper ──────────────────────────
  getProducts: (limit = 100) =>
    request<{ products: Product[]; limit: number; offset: number }>(`/api/products?limit=${limit}`),

  // ── Consignment (hàng ký gửi) ─────────────────────
  consignment: {
    list: (status?: string) => {
      const sp = new URLSearchParams();
      if (status) sp.set("status", status);
      return request<{ entries: ConsignmentEntry[] }>(`/api/consignment?${sp.toString()}`);
    },
    create: (data: {
      supplier_name: string;
      item_name: string;
      quantity_in: number;
      quantity_sold?: number;
      price_to_supplier: number;
      status?: string;
      note?: string;
    }) =>
      request<{ entry: ConsignmentEntry }>("/api/consignment", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<ConsignmentEntry>) =>
      request<{ entry: ConsignmentEntry }>(`/api/consignment/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/consignment/${id}`, { method: "DELETE" }),
    categoryMappings: {
      list: (supplierId: string) =>
        request<{ mappings: CategoryMappingEntry[] }>(`/api/consignment/supplier-categories?supplier_id=${supplierId}`),
      add: (data: { supplier_id: string; category_id: string; commission_rate: number; note?: string }) =>
        request<{ ok: boolean; mapping: CategoryMappingEntry }>("/api/consignment/supplier-categories", { method: "POST", body: JSON.stringify(data) }),
      remove: (id: number) =>
        request<{ ok: boolean }>(`/api/consignment/supplier-categories/${id}`, { method: "DELETE" }),
      report: (supplierId: string, from: string, to: string) =>
        request<ConsignmentCategoryReport>(`/api/consignment/report?supplier_id=${supplierId}&from=${from}&to=${to}`),
    },
  },

  // ── Consignment Items (SP ký gửi theo supplier) ──
  consignmentItems: {
    list: (supplierId: string) =>
      request<{ items: ConsignmentItem[]; discount_rate: number }>(`/api/consignment-items?supplier_id=${supplierId}`),
    add: (data: { item_ids: string[]; supplier_id: string; note?: string }) =>
      request<{ ok: boolean; inserted: number }>("/api/consignment-items", { method: "POST", body: JSON.stringify(data) }),
    remove: (id: number) =>
      request<{ ok: boolean }>(`/api/consignment-items/${id}`, { method: "DELETE" }),
    report: (supplierId: string, from?: string, to?: string) => {
      const sp = new URLSearchParams({ supplier_id: supplierId });
      if (from) sp.set("from", from);
      if (to) sp.set("to", to);
      return request<ConsignmentReport>(`/api/consignment-items/report?${sp.toString()}`);
    },
    overview: (from?: string, to?: string) => {
      const sp = new URLSearchParams();
      if (from) sp.set("from", from);
      if (to) sp.set("to", to);
      return request<ConsignmentOverview>(`/api/consignment-items/overview?${sp.toString()}`);
    },
    overviewSeries: (from?: string, to?: string) => {
      const sp = new URLSearchParams();
      if (from) sp.set("from", from);
      if (to) sp.set("to", to);
      return request<ConsignmentOverviewSeries>(`/api/consignment-items/overview-series?${sp.toString()}`);
    },
  },

  // ── Ops Todos ─────────────────────────────────────
  ops: {
    todos: {
      list: (params: { status?: string; category?: string } = {}) => {
        const sp = new URLSearchParams();
        if (params.status) sp.set("status", params.status);
        if (params.category) sp.set("category", params.category);
        return request<{ todos: OpsTodo[] }>(`/api/ops/todos?${sp.toString()}`);
      },
      create: (data: {
        title: string;
        description?: string;
        category?: string;
        priority?: string;
        due_date?: string;
      }) =>
        request<{ todo: OpsTodo }>("/api/ops/todos", { method: "POST", body: JSON.stringify(data) }),
      update: (id: string, data: Partial<OpsTodo>) =>
        request<{ todo: OpsTodo }>(`/api/ops/todos/${id}`, { method: "PUT", body: JSON.stringify(data) }),
      delete: (id: string) =>
        request<{ ok: boolean }>(`/api/ops/todos/${id}`, { method: "DELETE" }),
    },
    backup: {
      run: () =>
        request<{ ok: boolean; timestamp: string; size_bytes: number; counts: Record<string, number>; data: any }>(
          "/api/ops/backup",
          { method: "POST" }
        ),
      history: (limit = 50) =>
        request<{ history: BackupHistoryEntry[] }>(`/api/ops/backup/history?limit=${limit}`),
    },
  },

  // ── Backfill (điều chỉnh tồn kho) ─────────────────
  backfill: {
    list: (status?: string) => {
      const sp = new URLSearchParams();
      if (status) sp.set("status", status);
      return request<{ entries: BackfillEntry[] }>(`/api/backfill?${sp.toString()}`);
    },
    create: (data: {
      item_name: string;
      sku?: string;
      expected_qty: number;
      actual_qty: number;
      reason: string;
      note?: string;
    }) =>
      request<{ entry: BackfillEntry }>("/api/backfill", { method: "POST", body: JSON.stringify(data) }),
    approve: (id: string, approver?: string) =>
      request<{ entry: BackfillEntry }>(`/api/backfill/${id}/approve`, {
        method: "PUT",
        body: JSON.stringify({ approver }),
      }),
    update: (id: string, data: Partial<BackfillEntry>) =>
      request<{ entry: BackfillEntry }>(`/api/backfill/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/backfill/${id}`, { method: "DELETE" }),
  },

  // ── Assets (tài sản cố định) ──────────────────────
  assets: {
    list: (params: { status?: string; category?: string } = {}) => {
      const sp = new URLSearchParams();
      if (params.status) sp.set("status", params.status);
      if (params.category) sp.set("category", params.category);
      return request<{ assets: AssetEntry[] }>(`/api/assets?${sp.toString()}`);
    },
    create: (data: {
      name: string;
      category?: string;
      purchase_date: string;
      purchase_price: number;
      depreciation_years?: number;
      status?: string;
      note?: string;
    }) =>
      request<{ asset: AssetEntry }>("/api/assets", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<AssetEntry>) =>
      request<{ asset: AssetEntry }>(`/api/assets/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/assets/${id}`, { method: "DELETE" }),
  },

  // ── Smart Stock Alerts ───────────────────────────
  inventoryAlerts: () =>
    request<StockAlertsResponse>("/api/inventory/alerts"),

  // ── ABC Analysis (Pareto) ──────────────────────────
  inventoryABC: (days = 30) =>
    request<ABCResponse>(`/api/inventory/abc-analysis?days=${days}`),

  // ── Predictive Restock ────────────────────────────
  inventoryRestock: (days = 30) =>
    request<RestockResponse>(`/api/inventory/predictive-restock?days=${days}`),

  restockConfig: {
    update: (productId: string, data: { lead_time_days: number; safety_stock_days: number; notes?: string }) =>
      request<RestockConfig>(`/api/inventory/restock-config/${productId}`, { method: "PUT", body: JSON.stringify(data) }),
    reset: (productId: string) =>
      request<{ ok: boolean }>(`/api/inventory/restock-config/${productId}`, { method: "DELETE" }),
  },

  // ── Suppliers & Ratings (P2-11) ──────────────────
  suppliers: {
    list: (type?: "purchase" | "consignment") => request<{ suppliers: Supplier[] }>(`/api/suppliers${type ? `?type=${type}` : ""}`),
    create: (data: { name: string; phone?: string; email?: string; address?: string; notes?: string; supplier_type?: "purchase" | "consignment" }) =>
      request<{ ok: true; id: string }>("/api/suppliers", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: { name?: string; phone?: string; email?: string; address?: string; notes?: string; tax_id?: string; bank_account?: string }) =>
      request<{ ok: true }>(`/api/suppliers/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    orders: (id: string, params: { scope?: "all" | "supplier"; q?: string; limit?: number; offset?: number } = {}) => {
      const sp = new URLSearchParams();
      if (params.scope) sp.set("scope", params.scope);
      if (params.q) sp.set("q", params.q);
      if (params.limit !== undefined) sp.set("limit", String(params.limit));
      if (params.offset !== undefined) sp.set("offset", String(params.offset));
      const qs = sp.toString();
      return request<{ orders: SupplierOrder[]; total: number; limit: number; offset: number }>(`/api/suppliers/${id}/orders${qs ? "?" + qs : ""}`);
    },
    applyToOrders: (id: string, body: { order_ids: number[]; from_supplier_ids_to_retire?: string[] }) =>
      request<{ updated: number; retired: number; orders: SupplierOrder[] }>(`/api/suppliers/${id}/apply-to-orders`, { method: "POST", body: JSON.stringify(body) }),
    setType: (id: string, supplier_type: "purchase" | "consignment") =>
      request<{ ok: true; supplier_type: string }>(`/api/suppliers/${id}/type`, { method: "PUT", body: JSON.stringify({ supplier_type }) }),
    setDiscount: (id: string, discount_rate: number) =>
      request<{ ok: true; discount_rate: number; recalced: number; pushed: number; push_errors: { item_id: string; error: string }[] }>(
        `/api/suppliers/${id}/discount`, { method: "PUT", body: JSON.stringify({ discount_rate }) }),
    remove: (id: string) =>
      request<{ ok: true; unlinked?: number }>(`/api/suppliers/${id}`, { method: "DELETE" }),
    usage: (id: string) =>
      request<{ items_count: number; consignment_count: number }>(`/api/suppliers/${id}/usage`),
    ratings: {
      list: (supplierId: string) =>
        request<{ ratings: SupplierRating[] }>(`/api/suppliers/${supplierId}/ratings`),
      create: (supplierId: string, data: { import_order_id?: string; on_time_score: number; quality_score: number; price_score: number; comment?: string }) =>
        request<{ ok: true; id: string }>(`/api/suppliers/${supplierId}/ratings`, { method: "POST", body: JSON.stringify(data) }),
      remove: (supplierId: string, ratingId: string) =>
        request<{ ok: true }>(`/api/suppliers/${supplierId}/ratings/${ratingId}`, { method: "DELETE" }),
    },
    getAi: (id: string) =>
      request<SupplierAiConfig>(`/api/suppliers/${id}/ai`),
    setAi: (id: string, data: { provider: string; model: string }) =>
      request<{ ok: true }>(`/api/suppliers/${id}/ai`, { method: "PUT", body: JSON.stringify(data) }),
  },

  // ── Data Maturity ─────────────────────────────
  dataMaturity: () => request<DataMaturity>("/api/data-maturity"),

  // ── AI Config (P1.5) ─────────────────────────────
  ai: {
    configs: () =>
      request<{ configs: AIConfig[] }>("/api/ai/config"),
    createConfig: (data: { provider: string; api_key?: string; base_url?: string; model?: string; feature?: string; is_active?: boolean }) =>
      request<{ ok: boolean; id: string }>("/api/ai/config", { method: "POST", body: JSON.stringify(data) }),
    updateConfig: (id: string, data: Record<string, any>) =>
      request<{ ok: boolean }>(`/api/ai/config/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    deleteConfig: (id: string) =>
      request<{ ok: boolean }>(`/api/ai/config/${id}`, { method: "DELETE" }),
    test: (id: string) =>
      request<{ ok: boolean; response: string }>(`/api/ai/test/${id}`, { method: "POST" }),
    scanModels: (id: string) =>
      request<{ models: { id: string; name: string; size?: number; provider: string }[]; provider: string }>(`/api/ai/config/${id}/models`),
    translate: (text: string, target_lang = 'vi') =>
      request<{ original: string; translated: string }>("/api/ai/translate", { method: "POST", body: JSON.stringify({ text, target_lang }) }),
    suggestPrice: (data: { item_name: string; cost?: number; category?: string; competitor_prices?: string }) =>
      request<{ suggested_price?: number; reason?: string; raw?: string }>("/api/ai/suggest-price", { method: "POST", body: JSON.stringify(data) }),
    query: (question: string, history?: Array<{ role: string; content: string }>, image?: { base64: string; mime_type: string }, files?: Array<{ base64: string; mime_type: string; name: string }>) =>
      request<{ ok: boolean; answer: string; pendingAction?: AgentPendingAction | null; sql?: string; rows?: any[]; provider: string; model: string }>("/api/ai/query", { method: "POST", body: JSON.stringify({ question, history, image, files }) }),
    applyAction: (action: any) =>
      request<{ ok: boolean; message: string }>("/api/ai/apply-action", { method: "POST", body: JSON.stringify({ action }) }),

    // ── Multi-provider KV schema (mới) ──────────────
    configMulti: () => request<AIConfigMulti>("/api/ai/config-multi"),
    setActive: (active: string, model?: string) =>
      request<{ ok: boolean }>("/api/ai/config-multi/active", { method: "PUT", body: JSON.stringify({ active, model }) }),
    setRoles: (data: {
      active?: string;
      model?: string;
      lead_provider?: string | null;
      lead_model?: string | null;
      culy_provider?: string | null;
      culy_model?: string | null;
      vision_provider?: string | null;
      vision_model?: string | null;
    }) =>
      request<{ ok: boolean }>("/api/ai/config-multi/roles", { method: "PUT", body: JSON.stringify(data) }),
    upsertProvider: (name: string, data: { api_key?: string; api_keys?: string[]; base_url?: string }) =>
      request<{ ok: boolean }>(`/api/ai/config-multi/provider/${name}`, { method: "PUT", body: JSON.stringify(data) }),
    removeProvider: (name: string) =>
      request<{ ok: boolean }>(`/api/ai/config-multi/provider/${name}`, { method: "DELETE" }),
    scanMulti: (name: string) =>
      request<{ models: { id: string; name: string; size?: number }[] }>(`/api/ai/config-multi/scan/${name}`, { method: "POST" }),
    revealKey: (name: string) =>
      request<{ api_key: string; api_keys: string[] }>(`/api/ai/config-multi/reveal/${name}?_t=${Date.now()}`),
  },

  // ── Materials (W10) ────────────────────────────
  materials: {
    bulkImport: (items: BulkImportMaterial[]) =>
      request<{ ok: boolean; count: number; total: number; errors: { name: string; error: string }[] }>("/api/materials/bulk-import", {
        method: "POST",
        body: JSON.stringify({ items }),
      }),
    /** Xuất dùng nguyên liệu (trừ tồn FIFO).
     *  BE res: `{ ok, consumed, remaining_stock }` (200)
     *  hoặc 500 `{ error, consumed, remaining_stock }` (race/insufficient). */
    consumeMaterial: (
      item_id: string,
      quantity: number,
      reason: string,
      source: "manual" | "waste" | "internal" | "return_to_supplier" = "manual",
    ) =>
      request<{ ok: boolean; consumed: number; remaining_stock: number }>("/api/stock-lots/consume", {
        method: "POST",
        body: JSON.stringify({ item_id, quantity, reason, source }),
      }),
    /** Báo cáo xuất dùng nguyên liệu trong khoảng [from, to] (ISO date).
     *  KHÔNG có `reports[]`/`total_value` — BE trả `materials_consumed[]` +
     *  composite_revenue_vnd/total_material_cogs_vnd/cogs_source_check/gross_profit_material_vnd/warnings/note. */
    getReport: (from?: string, to?: string) => {
      const sp = new URLSearchParams();
      if (from) sp.set("from", from);
      if (to) sp.set("to", to);
      return request<IMaterialReport>(
        `/api/materials/report?${sp.toString()}`,
      );
    },
    /** Import thêm NL vào lot (POST /api/stock-lots).
     *  Payload khớp BE: `{ item_id, variant_id?, import_order_id?, quantity, unit_cost?, is_vat?, is_bangke?, vat_invoice_id? }`.
     *  BE không hỗ trợ lot_date/expiry_date/supplier_id/source/reason ở /api/stock-lots (chỉ /api/materials/bulk-import có expiry). */
    importExtra: (data: {
      item_id: string;
      variant_id?: string;
      import_order_id?: number;
      quantity: number;
      unit_cost?: number;
      is_vat?: number;
      is_bangke?: number;
      vat_invoice_id?: number;
    }) =>
      request<{ lot: IMaterialLotExtended }>("/api/stock-lots", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  // ── Sync Google Sheets (W9) ────────────────────
  syncSheets: {
    toRecipes: () => request<{ ok: boolean; count?: number; message?: string }>("/api/sync/sheets-to-recipes", { method: "POST" }),
    rebuildAll: () => request<{ ok: boolean; count?: number; message?: string }>("/api/sync/sheets-rebuild-all", { method: "POST" }),
  },

  // ── Recipes (W7) ────────────────────────────────
  recipes: {
    get: (itemId: string) =>
      request<Recipe>(`/api/recipes/${itemId}`),
    update: (itemId: string, items: { material_item_id: string; qty_per_unit: number; unit?: string; note?: string }[]) =>
      request<{ ok: boolean; count: number; total_cogs: number }>(`/api/recipes/${itemId}`, {
        method: "PUT",
        body: JSON.stringify({ items }),
      }),
    searchMaterials: (q: string) =>
      request<RecipeMaterialSearch[]>(`/api/recipes/materials/search?q=${encodeURIComponent(q)}`),
  },

  // ── Print history (lịch sử in tem) ────────────────
  // SEAM: worker có thể chưa ship → 404/405 nghĩa là "chưa có", KHÔNG hard-fail.
  printHistory: {
    create: (body: PrintHistoryCreateRequest) =>
      request<PrintHistoryCreateResponse>("/api/print-history", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    list: (params: PrintHistoryQuery = {}) => {
      const sp = new URLSearchParams();
      if (params.limit != null) sp.set("limit", String(params.limit));
      if (params.q) sp.set("q", params.q);
      const qs = sp.toString();
      return request<PrintHistoryResponse>(`/api/print-history${qs ? `?${qs}` : ""}`);
    },
    /** Xoá 1 lần in. 404/405 = worker chưa ship → UI báo chờ, KHÔNG hard-fail. */
    remove: (id: number) =>
      request<PrintHistoryDeleteResponse>(`/api/print-history/${id}`, { method: "DELETE" }),
    /**
     * Xoá TOÀN BỘ lịch sử in — cố ý KHÁC `remove`: path riêng + POST + body bắt buộc
     * `{ all: true }`, để không thể vô tình kích hoạt bằng 1 lệnh DELETE trần.
     */
    clearAll: () =>
      request<PrintHistoryClearAllResponse>("/api/print-history/clear-all", {
        method: "POST",
        body: JSON.stringify({ all: true } satisfies PrintHistoryClearAllRequest),
      }),
    /**
     * Xoá MỘT SỐ lần in đã tick — cùng thế an toàn với clearAll: POST + body tường minh,
     * `ids` bắt buộc và không rỗng. KHÔNG dùng DELETE-kèm-body.
     */
    removeMany: (ids: number[]) =>
      request<PrintHistoryDeleteManyResponse>("/api/print-history/delete", {
        method: "POST",
        body: JSON.stringify({ ids } satisfies PrintHistoryDeleteManyRequest),
      }),
  },
};

export interface SyncLog {
  id: number;
  sync_type: string;
  status: "ok" | "error";
  started_at: string;
  finished_at: string;
  rows_upserted: number;
  message: string | null;
}

export interface CategoryRevenue {
  category: string;
  color: string;
  revenue: number;
  qty: number;
}

export interface CategoryProduct {
  item_id: string;
  item_name: string;
  sku: string;
  qty: number;
  revenue: number;
  /** Tồn hiện tại của SP. Optional: worker cũ chưa trả field này → ẩn phần "tồn". */
  stock?: number;
}

export interface EmployeeRevenue {
  employee: string;
  employee_id: string | null;
  orders: number;
  revenue: number;
}

export interface InventoryStats {
  variants: number;
  out_of_stock: number;
  low_stock: number;
  stock_cost_value: number;
  stock_retail_value: number;
  total_items: number;
}

export interface DashboardToday {
  date: string;
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  totalDiscount: number;
  deltaSales: number | null;
  deltaOrders: number | null;
  topItems: { item_id: string; item_name: string; sku: string; qty: number; revenue: number }[];
  recentOrders: { receipt_number: string; receipt_date: string; total_money: number; payment_type: string }[];
  hourly: { hour: number; orders: number; sales: number }[];
}

export interface DashboardSummary {
  period: { from: string; to: string; days: number };
  totalSales: number;
  totalOrders: number;
  totalDiscount: number;
  averageOrderValue: number;
  deltaSales: number | null;
  deltaOrders: number | null;
  prevTotalSales: number;
  hourly: { hour: number; orders: number; sales: number }[];
  topItems: { item_id: string; item_name: string; sku: string; qty: number; revenue: number }[];
  recentOrders: { receipt_number: string; receipt_date: string; total_money: number; payment_type: string }[];
  categories: CategoryRevenue[];
}

export interface Order {
  receipt_number: string;
  receipt_date: string;
  total_money: number;
  total_tax: number;
  total_discount: number;
  payment_type: string;
  customer_id: string | null;
  employee_id: string | null;
  receipt_type?: string;
  cancelled_at?: string | null;
  refund_for?: string | null;
}

export interface ReceiptItem {
  id: string;
  receipt_number: string;
  item_id: string;
  variant_id: string;
  item_name: string;
  variant_name: string | null;
  sku: string;
  quantity: number;
  price: number;
  cost: number;
  cost_total: number;
  gross_total: number;
  total_money: number;
  total_discount: number;
  line_note: string | null;
  receipt_date: string;
  store_id: string;
}

export type VerificationSource = "loyverse_d1" | "d1_local";

export interface BulkOperationResult {
  updated: number;
  submitted: number;
  failed: Array<{ item_id: string; error: string }>;
  request_id?: string;
  results?: Array<{
    item_id: string;
    ok: boolean;
    operation: string;
    verified_value?: string | null;
    verification_source?: VerificationSource;
    error?: string;
    reconciliation_required?: boolean;
  }>;
}

export interface BulkItemUpdateRequest {
  item_ids: string[];
  operation: "category_set" | "price_set" | "price_percent" | "vat_set" | "bangke_set" | "import_date_set";
  value: unknown;
  request_id?: string;
}

export interface BulkProductSaleRequest {
  item_ids: string[];
  action: "create" | "set_expiry" | "end";
  mode?: "fixed" | "percent";
  value?: number;
  ends_at?: string | null;
  request_id?: string;
}

export interface Product {
  id: string;
  item_name: string;
  description: string | null;
  category_id: string | null;
  category_name?: string | null;
  image_url: string | null;
  track_stock: number;
  updated_at: string;
  sku?: string | null;
  barcode?: string | null;
  default_price?: number | null;
  cost?: number | null;
  in_stock?: number | null;
  variant_id?: string | null;
  item_created_at?: string;
  import_date?: string | null;
  stock_updated_at?: string;
  is_composite?: number;
  /** Catalog sale overlay (POS dual-run); optional on list. */
  list_price_vnd?: number | null;
  price_vnd?: number | null;
  is_sale?: number | null;
  sale_ends_at?: string | null;
  /** Pre-merge (alias) values; present only when the merge schema exists. */
  alias_item_names?: string[];
  alias_skus?: string[];
  alias_barcodes?: string[];
}

/** Row from GET /api/product-sales (pos_product_sales + item/sku join). */
export interface ProductSale {
  id: number;
  variant_id: string;
  item_id: string;
  list_price: number;
  sale_price: number;
  starts_at: string;
  ends_at: string | null;
  status: "active" | "ended" | string;
  lv_patched?: number;
  note: string | null;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  item_name?: string | null;
  sku?: string | null;
  barcode?: string | null;
}

// ── Recipe types (W7) ──────────────────────────────
export interface RecipeMaterial {
  material_item_id: string;
  material_name: string;
  qty_per_unit: number;
  unit: string;
  unit_cost: number;
  subtotal: number;
  note: string;
  qty_base?: number | null;
  base_unit?: string;
}

export interface Recipe {
  item_id: string;
  item_name: string;
  is_composite: number;
  materials: RecipeMaterial[];
  total_cogs: number;
  warnings: string[];
}

export interface BulkImportMaterial {
  name: string;
  unit?: string;
  base_unit?: string;
  supplier_id?: string;
  note?: string;
  cost: number;
  stock_qty: number;
  import_date?: string;
  expiry_date?: string;
}

/** Stock lot mở rộng — trả về từ GET /api/stock/materials/:itemId/lots.
 *  `quantity` = qty nhập gốc, `remaining` = còn lại sau FIFO tiêu hao (BE tính). */
export interface IMaterialLotExtended {
  id: number;
  item_id: string;
  quantity: number;
  remaining?: number;
  unit_cost: number;
  unit?: string;
  created_at: string;
  expiry_date?: string | null;
  source?: "manual" | "import_order" | null;
  reason?: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  import_order_id?: number | null;
  order_date?: string | null;
  goods_type?: string | null;
  is_vat?: number;
  is_bangke?: number;
  exhausted_at?: string | null;
}

/** Báo cáo xuất dùng nguyên liệu trong khoảng thời gian (GET /api/materials/report).
 *  KHÔNG có `reports[]`/`total_value` — BE trả đúng shape này. */
export interface IMaterialReport {
  from: string;
  to: string;
  materials_consumed: {
    material_item_id: string;
    material_name: string;
    consumed_qty: number;
    consumed_cost_vnd: number;
  }[];
  composite_revenue_vnd: number;
  total_material_cogs_vnd: number;
  cogs_source_check: number;
  gross_profit_material_vnd: number;
  warnings: string[];
  note: string;
}

export interface RecipeMaterialSearch {
  id: string;
  item_name: string;
  cost: number;
  base_unit?: string;
}

export interface Category {
  id: string;
  name: string;
  color: string | null;
  parent_id?: string | null;
  item_count?: number;
  has_recipe?: number;
}

export interface CategoryItemRef {
  id: string;
  item_name: string;
  sku: string | null;
}

export interface CategoryItemsResponse {
  category: { id: string; name: string };
  items: CategoryItemRef[];
  total: number;
}

export interface SearchItem {
  id: string;
  item_name: string;
  sku?: string;
  default_price?: number;
  cost?: number;
  category_name?: string;
}

export interface Variant {
  variant_id: string;
  item_id: string;
  sku: string;
  barcode: string | null;
  default_price: number;
  cost: number;
  in_stock?: number;
}

export interface InventoryRow {
  item_id: string;
  item_name: string;
  variant_id: string;
  sku: string;
  barcode: string | null;
  default_price: number;
  cost?: number;
  variant_name?: string;
  in_stock: number;
  updated_at: string;
  stock_updated_at?: string;
  category_id?: string | null;
  category_name?: string | null;
  import_date?: string | null;
  description?: string;
  image_url?: string;
  is_vat?: boolean | number;
  is_bangke?: boolean | number;
}

export interface VirtualRevenueRange {
  from: string;
  to: string;
}

export interface VirtualRevenueWarning {
  code: string;
  message: string;
  item_name?: string | null;
  import_order_id?: number | null;
  stock_lot_id?: number;
}

export interface VirtualRevenueCapacityLot {
  stock_lot_id: number;
  import_order_id: number;
  import_order_item_id: number;
  item_id: string;
  variant_id: string | null;
  item_name: string;
  sku: string | null;
  sub_type: "vat" | "bangke";
  created_at: string;
  source_qty: number;
  fifo_consumed_qty: number;
  legacy_backfill_qty: number;
  source_capacity_qty: number;
  active_allocated_qty: number;
  available_qty: number;
  unit_sale_price: number;
  available_value: number;
}

export interface VirtualRevenueCapacity {
  range: VirtualRevenueRange;
  revenue_metric: "canonical_sales_revenue_minus_active_virtual_allocations";
  source_revision: number;
  canonical_sales_revenue: number;
  active_allocated_revenue: number;
  canonical_unallocated_revenue: number;
  available_virtual_value: number;
  max_allocatable_revenue: number;
  lots: VirtualRevenueCapacityLot[];
  warnings: VirtualRevenueWarning[];
}

export interface VirtualRevenuePreviewItem extends VirtualRevenueCapacityLot {
  qty: number;
  allocated_value: number;
  is_adjusted_final: boolean;
  available_after_qty: number;
}

export interface VirtualRevenuePreview extends VirtualRevenueCapacity {
  target_revenue: number;
  allocated_revenue: number;
  plan_hash: string;
  entry_date: string;
  items: VirtualRevenuePreviewItem[];
}

export interface VirtualRevenuePreviewRequest {
  from: string;
  to: string;
  target_revenue: number;
}

export interface VirtualRevenueApplyRequest extends VirtualRevenuePreviewRequest {
  plan_hash: string;
  idempotency_key: string;
  reason: string;
}

export interface VirtualRevenueAllocationItem {
  id: number;
  allocation_id: string;
  stock_lot_id: number;
  import_order_item_id: number;
  item_id: string;
  variant_id: string | null;
  item_name: string;
  sku: string | null;
  sub_type: "vat" | "bangke";
  source_qty: number;
  fifo_consumed_qty_snapshot: number;
  legacy_backfill_qty_snapshot: number;
  source_capacity_qty: number;
  qty: number;
  unit_sale_price_snapshot: number;
  allocated_value: number;
  is_adjusted_final: number | boolean;
  created_at: string;
}

export interface VirtualRevenueAllocationEvent {
  id: string;
  allocation_id: string;
  event_type: "allocate" | "reverse";
  reverses_event_id: string | null;
  idempotency_key: string;
  payload_hash: string;
  payload_json: string | null;
  actor: string;
  reason: string;
  created_at: string;
}

export interface VirtualRevenueAllocation {
  id: string;
  store_id: string;
  date_from: string;
  date_to: string;
  entry_date: string;
  revenue_metric: string;
  source_revision: number;
  source_revenue: number;
  target_revenue: number;
  allocated_revenue: number;
  status: "active" | "reversed";
  reason: string;
  plan_hash: string;
  idempotency_key: string;
  created_by: string;
  created_at: string;
  reversed_by: string | null;
  reversed_at: string | null;
  reversal_reason: string | null;
  reversed_event_id: string | null;
  s1a_regen_status: "pending" | "ready" | "error";
  s1a_regen_error: string | null;
  s1a_regenerated_at: string | null;
  item_count?: number;
  total_qty?: number;
  items?: VirtualRevenueAllocationItem[];
  events?: VirtualRevenueAllocationEvent[];
}

export interface VirtualRevenueAllocationFilters {
  id?: string;
  status?: "active" | "reversed";
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface VirtualRevenueAllocationListResponse {
  allocations: VirtualRevenueAllocation[];
  limit: number;
  offset: number;
}

export interface VirtualRevenueS1aRegen {
  status: "pending" | "ready" | "error" | string;
  dates: string[];
  error?: string;
}

export interface VirtualRevenueApplyResponse {
  ok: true;
  allocation: VirtualRevenueAllocation;
  preview?: VirtualRevenuePreview;
  idempotent_replay: boolean;
  affected_dates: string[];
  s1a_regen: VirtualRevenueS1aRegen;
}

export interface VirtualRevenueReverseRequest {
  idempotency_key: string;
  reason: string;
}

export interface VirtualRevenueReverseResponse {
  ok: true;
  allocation: VirtualRevenueAllocation;
  event: VirtualRevenueAllocationEvent;
  idempotent_replay: boolean;
  affected_dates: string[];
  s1a_regen: VirtualRevenueS1aRegen;
}

export interface SalesHistoryRow {
  id: number;
  sale_date: string;
  item_name: string;
  sku: string | null;
  qty: number;
  unit_price: number;
  revenue: number;
  cost: number;
  category: string | null;
  source: string;
  imported_at: string;
  sold_at?: string | null;
  receipt_number?: string | null;
  sale_time?: string | null;
}

export interface SalesHistoryStats {
  total_revenue: number;
  total_cost: number;
  gross_profit: number;
  distinct_items: number;
  top_skus: { sku: string; rev: number }[];
  top_categories: { category: string; rev: number }[];
}

// ═══════════════════════════════════════════════════
// New types for Phase B pages
// ═══════════════════════════════════════════════════

export interface TaxEligibility {
  year: number;
  revenue_total: number;
  tax_status: "exempt" | "fixed" | "declared";
  tax_rate: number;
  tax_amount: number;
  thresholds: { exempt: number; fixed?: number };
  requires_mtt_invoice?: boolean;
  legal_basis?: string;
}

export interface TaxReport {
  filing: Record<string, unknown> | null;
  revenue_total: number;
  expense_total: number;
  taxable_amount: number;
  tax_status: "exempt" | "fixed" | "declared";
  tax_rate: number;
  tax_amount: number;
  s1a_revenue_year?: number;
  s1a_revenue_quarter?: number;
  quarter_range: { from: string; to: string };
  s1a_entries: S1aEntry[];
}

export interface TaxFiling {
  id: number;
  year: number;
  quarter: number;
  total_revenue: number;
  total_expense: number;
  taxable_amount: number;
  tax_rate: number;
  tax_amount: number;
  tax_status: string;
  is_locked: number;
  status?: string;
  filed_at?: string;
  created_at?: string;
}

export interface LockPeriodResult {
  lock: Record<string, unknown>;
}

export interface ItemCreate {
  item_name: string;
  description?: string;
  category_id?: string;
  image_url?: string;
  track_stock?: boolean;
  form?: string;
  color?: string;
  is_vat?: number;
  is_bangke?: number;
  is_composite?: number;
  variants: {
    variant_name?: string;
    sku?: string;
    barcode?: string;
    default_price: number;
    cost?: number;
    initial_stock?: number;
    supplier_name?: string;
  }[];
}

export interface ItemUpdate {
  item_name?: string;
  description?: string;
  category_id?: string;
  image_url?: string;
  track_stock?: boolean;
  form?: string;
  color?: string;
  is_vat?: number;
  is_bangke?: number;
  is_composite?: number;
  import_date?: string;
  import_qty?: number | null;
  variants?: {
    variant_id?: string;
    variant_name?: string;
    sku?: string;
    barcode?: string;
    default_price?: number;
    cost?: number;
    initial_stock?: number;
    _delete?: boolean;
  }[];
}

export interface ItemDetail {
  item: Product & { form?: string; color?: string; track_stock?: number };
  variants: (Variant & { in_stock: number; is_vat?: number; is_bangke?: number; variant_name?: string })[];
  audit_log: { id: number; item_id: string; action: string; field_changed: string | null; old_value: string | null; new_value: string | null; changed_by: string; changed_at: string; source: string }[];
  stock_lots: { id: number; item_id: string; variant_id: string | null; quantity: number; unit_cost: number; is_vat: number; is_bangke: number; note: string | null; created_at: string }[];
}

export interface ItemHistory {
  product: { id: string; item_name: string; sku: string | null; barcode: string | null; default_price: number; cost: number; in_stock: number; created_at: string | null; category_name: string | null };
  initial_lots: Array<{ lot_id: number; quantity: number; unit_cost: number; created_at: string | null; source: string | null; reason: string | null }>;
  imports: Array<{ import_order_id: number; order_date: string | null; supplier_name: string | null; goods_type: string | null; status: string | null; line_name: string | null; quantity: number; unit_cost: number; lot_created_at: string | null; lot_exhausted_at: string | null }>;
  sales: { qty: number; revenue: number; receipt_count: number; first_at: string | null; last_at: string | null };
  sheet_sales: { qty: number; revenue: number; first: string; last: string } | null;
  adjustments: Array<{ changed_at: string; action: string; old_value: string | null; new_value: string | null; delta: number; changed_by: string | null; source: string | null }>;
  reconciliation: { initial: number; imported: number; sold: number; adjusted: number; expected: number; actual: number; delta: number };
  material_note: boolean;
}

export interface InventoryAdjustResult {
  variant_id: string;
  old_stock: number;
  new_stock: number;
  delta: number;
  reason?: string;
}

export interface BulkImportResult {
  imported: number;
  total: number;
  errors: string[];
  created?: number;
}

export interface Expense {
  id: number;
  expense_date: string;
  category: string;
  description: string | null;
  amount: number;
  attachment_url?: string | null;
  source?: string;
  created_by?: string;
}

export interface Payable {
  id: number;
  import_order_id?: number | null;
  supplier_name: string;
  supplier_phone?: string | null;
  supplier_bank_account?: string | null;
  total_amount: number;
  paid_amount: number;
  remaining: number;
  due_date: string | null;
  status: "open" | "partial" | "paid";
  note?: string | null;
  created_at?: string;
  import_supplier_name?: string;
  import_order_date?: string;
}

export interface PurchaseLedgerEntry {
  id: number;
  date: string;
  supplier_name: string;
  invoice_number?: string;
  amount: number;
  vat_amount: number;
  is_vat: boolean;
  is_bangke: boolean;
  import_order_id?: number | null;
  created_at: string;
}

export interface VatInvoice {
  id: number;
  import_order_id?: number | null;
  invoice_number: string;
  supplier_name?: string;
  seller_name?: string;
  seller_tax_code?: string | null;
  amount?: number;
  amount_before_vat?: number;
  vat_amount: number;
  vat_rate?: number;
  total_amount?: number;
  attachment_url?: string | null;
  invoice_date: string;
  created_at: string;
}

/** GET /api/import-orders/analytics — đơn ĐÃ NHẬP (imported) theo order_date, toàn bộ dữ liệu. */
export interface ImportOrderAnalytics {
  summary: {
    order_count: number;
    invoice_value: number;
    paid_value: number;
    remaining_value: number;
    pending_count: number;
  };
  trend: Array<{ date: string; order_count: number; invoice_value: number }>;
  suppliers: Array<{ supplier_name: string; order_count: number; invoice_value: number }>;
  goods_types: Array<{ goods_type: string; order_count: number; invoice_value: number }>;
}

export interface ImportOrder {
  id: number;
  order_date: string;
  supplier_name: string | null;
  supplier_id?: string | null;
  supplier_phone?: string | null;
  supplier_address?: string | null;
  supplier_tax_id?: string | null;
  supplier_bank_account?: string | null;
  total_amount: number;
  paid_amount: number;
  currency: string;
  exchange_rate: number;
  due_date?: string | null;
  status: "pending" | "imported" | "cancelled" | "draft";
  note?: string | null;
  goods_type?: "vn" | "vat" | "bangke" | "tieungach";
  vat_rate?: number;
  invoice_no?: string | null;
  invoice_file_id?: string | null;
  invoice_file_type?: string | null;
  created_by?: string;
  created_at?: string;
  /** MAX(stock_lots.created_at) — ngày nhập kho (NULL nếu chưa nhập). */
  received_at?: string | null;
  items?: ImportOrderItem[];
}

export interface ImportOrderItem {
  id?: number;
  item_id?: string;
  mapped_item_id?: string | null;
  item_name: string;
  sku?: string | null;
  quantity: number;
  unit_cost: number;
  subtotal?: number;
  note?: string | null;
  is_vat: boolean;
  is_bangke: boolean;
  sell_price?: number | null;
  is_new_product?: boolean;
  mapped_item_name?: string | null;
  /** NL (nguyên liệu) — D1-only, KHÔNG đẩy Loyverse POS */
  is_material?: boolean;
  /** Đơn vị tính NL: kg/g/ml/l/pcs */
  unit?: string | null;
  /** Danh mục SP — dùng khi tạo SP mới (HÀNG MỚI / SP mới) để gán items.category_id */
  category_id?: string | null;
  /** SP gốc đang nhập kho khi dòng thuộc nhóm gộp đang hoạt động. null = không gộp. */
  merge_target?: { item_id: string; item_name: string; sku: string | null } | null;
}

/** PATCH /api/import-orders/:id → 409 khi dòng đã gộp không cho sửa field khóa. */
export interface ImportOrderConflict {
  error: string;
  code?: string;
  reasons?: Array<{ line_id: string | null; code: string; message: string }>;
}

export interface ImportOrderAttachment {
  id: string;
  file_name: string;
  drive_link: string;
  mime_type: string;
  uploaded_at: string;
}

export interface VatBackfillRow {
  receipt_number: string;
  receipt_item_id: string;
  receipt_date: string;
  item_name: string;
  sale_qty: number;
  alloc_qty: number;
  locked: boolean;
}

export interface VatBackfillResult {
  mapped_item_id: string;
  sub_type: "vat" | "bangke";
  purchased_qty: number;
  already_allocated: number;
  available_qty: number;
  total_sold_qty: number;
  current_stock: number;
  rows: VatBackfillRow[];
  allocated_total: number;
  affected_dates: string[];
  locked_dates: string[];
}

export interface ScannedInvoice {
  supplier_name: string | null;
  supplier_address: string | null;
  supplier_tax_id: string | null;
  supplier_phone: string | null;
  supplier_bank_account: string | null;
  invoice_no: string | null;
  order_date: string | null;
  currency: string;
  total_amount: number;
  shipping_fee?: number;
  items: Array<{ item_name: string; sku: string | null; quantity: number; unit_cost: number; subtotal: number; pack_count?: number | null }>;
}

export interface AgentDiffField {
  scope: "item" | "variant";
  variant_id?: string;
  variant_label?: string;
  key: string;
  label: string;
  old: any;
  new: any;
  selected?: boolean;
}

export type AgentBulkRow = { item_id: string; variant_id?: string; item_name: string; sku?: string; old: any; new: any; selected: boolean };
export type AgentCreateRow = { item_name: string; default_price: number; cost: number; initial_stock: number; sku?: string; barcode?: string; category_name?: string; selected: boolean; existing_item_id?: string; existing_item_name?: string };
export type AgentImportRow = { item_name: string; quantity: number; unit_cost: number; sku?: string; selected: boolean };
export type AgentPendingAction =
  | { type: "update_product"; item_id: string; item_name: string; fields: AgentDiffField[] }
  | { type: "create_product"; category_name?: string; rows: AgentCreateRow[] }
  | { type: "create_import_order"; supplier_name?: string; order_date?: string; paid_amount: number; currency: string; rows: AgentImportRow[]; attachments?: Array<{ base64: string; mime_type: string; name: string }> }
  | { type: "delete_product"; item_id: string; item_name: string }
  | { type: "bulk_action"; op: string; label: string; value?: number; category_id?: string; category_name?: string; rows: AgentBulkRow[] }
  | { type: "mark_s1a"; tax_type: string; label: string; rows: AgentBulkRow[] }
  | { type: "add_expense"; amount: number; category: string; description: string }
  | { type: "add_asset"; name: string; purchase_price: number; category: string; purchase_date?: string; depreciation_years: number; note?: string }
  | { type: "create_todo"; title: string; description?: string; priority?: string; due_date?: string }
  | { type: "map_import_items"; import_order_id: number; rows: Array<{ import_order_item_id: number; line_item_name: string; mapped_item_id: string; mapped_item_name: string }>; new_product_item_ids?: number[] }
  | { type: "batch_match_import_items"; import_order_id: number; results: Array<{ line_id: number; line_name: string; best_match?: { item_id: string; item_name: string; score: number; matched_on: string }; candidates?: Array<{ item_id: string; item_name: string; score: number; matched_on: string }>; is_new: boolean; confidence: "high" | "medium" | "low" }>; all_confident: boolean }
  | { type: "upload_product_image"; item_id: string; item_name: string; image?: { base64: string; mime_type: string } }
  | { type: "pay_payable"; payable_id: number; supplier_name: string; amount: number; remaining_after: number; payment_method: string; note?: string }
  | { type: "receive_import"; import_order_id: number; supplier_name?: string; rows: Array<{ item_id: string; variant_id: string | null; item_name: string; quantity: number; unit_cost: number; is_vat: number; is_bangke: number }>; unmapped: string[] }
  | { type: "adjust_stock"; item_id: string; variant_id: string; item_name: string; old_stock: number; new_stock: number; reason: string }
  | { type: "stock_take"; rows: Array<{ item_id: string; variant_id?: string | null; item_name: string; old_stock: number; actual_qty: number; delta: number }> }
  | { type: "add_supplier"; name: string; phone?: string; address?: string; note?: string; existing_id?: number }
  | { type: "add_category"; name: string }
  | { type: "delete_import_order"; import_order_id: number; supplier_name?: string; item_count: number; total_amount: number }
  | { type: "add_purchase_ledger"; description: string; amount: number; category?: string; purchase_date?: string }
  | { type: "update_supplier"; supplier_id: number; name?: string; phone?: string; address?: string; tax_id?: string; bank_account?: string; delete?: boolean }
  | { type: "rate_supplier"; supplier_id: number; supplier_name?: string; delivery?: number; quality?: number; price?: number; note?: string }
  | { type: "manage_expense"; expense_id: number; amount?: number; category?: string; note?: string; date?: string; delete?: boolean }
  | { type: "manage_asset"; asset_id: number; name?: string; value?: number; note?: string; delete?: boolean }
  | { type: "manage_todo"; todo_id: number; title?: string; status?: string; delete?: boolean }
  | { type: "manage_purchase_ledger"; ledger_id: number; seller_name?: string; seller_address?: string; seller_id_card?: string; item_description?: string; quantity?: number; unit_price?: number; total_amount?: number; delete?: boolean; warn_synced?: boolean }
  | { type: "manage_vat_invoice"; invoice_id: number; invoice_number?: string; invoice_date?: string; seller_name?: string; seller_tax_id?: string; amount_before_vat?: number; vat_amount?: number; delete?: boolean; warn_synced?: boolean }
  | { type: "assign_consignment"; item_ids?: number[]; item_id?: number; supplier_id: number; supplier_name?: string; commission_rate?: number; note?: string; count?: number }
  | { type: "settle_consignment"; supplier_id: number; supplier_name?: string; period_from?: string; period_to?: string; revenue?: number; payable_amount?: number; commission_rate?: number };

// Keep old VirtualOrderItem for backward compat but mark deprecated
export interface VirtualOrderItem {
  item_id: string;
  item_name: string;
  sku?: string;
  is_vat: boolean;
  is_bangke: boolean;
  in_stock: number;
  default_price: number;
}

export interface HkdInfo {
  name: string;
  tax_code: string;
  address: string;
  owner?: string;
  preparer?: string;
  business_location?: string;
  business_line?: string;
}

export interface S1aEntry {
  id: number;
  entry_date: string;
  description: string;
  sub_type: "vat" | "bangke";
  revenue: number;
  cost: number;
  source_virtual_order_id?: number | null;
}

export interface VirtualOrder {
  id: number;
  source_receipt_id: string;
  virtual_type: "vat" | "bangke";
  total_revenue: number;
  total_cost: number;
  gross_profit: number;
  receipt_date: string;
  item_names?: string;
}

export interface VirtualInventoryItem {
  item_id: string;
  item_name: string;
  variant_id: string;
  sku: string | null;
  barcode: string | null;
  default_price: number;
  in_stock: number;
  is_vat: number;
  is_bangke: number;
  updated_at: string;
}

export interface StockLot {
  id: number;
  item_id: string;
  lot_date: string;
  quantity: number;
  remaining: number;
  unit_cost: number;
  expiry_date?: string;
  created_at: string;
}

export interface StockAlertSetting {
  item_id: string;
  item_name: string;
  threshold: number;
  current_stock: number;
  enabled: boolean;
}

export interface StockAlertList {
  alerts: StockAlertSetting[];
  total: number;
}

export interface StockTakeSession {
  id: number;
  session_date: string;
  notes?: string | null;
  status: "open" | "completed";
  total_items?: number;
  diff_count?: number;
  created_by?: string;
  created_at: string;
  completed_at?: string | null;
}

export interface StockTakeItem {
  id: number;
  session_id: number;
  item_id: string;
  variant_id?: string | null;
  item_name: string;
  sku?: string;
  expected_qty: number;
  actual_qty: number | null;
  reason?: string | null;
}

export interface ComparisonData {
  period: string;
  days: number;
  this_period: Array<{ date: string; revenue: number; orders: number }>;
  prev_period: Array<{ date: string; revenue: number; orders: number }>;
}

// ═══════════════════════════════════════════════════
// Consignment / Ops Todos / Backfill / Assets / Backup
// ═══════════════════════════════════════════════════

export interface ConsignmentEntry {
  id: string;
  supplier_name: string;
  item_name: string;
  quantity_in: number;
  quantity_sold: number;
  price_to_supplier: number;
  status: "pending" | "partial" | "settled";
  note: string | null;
  created_at: string;
  updated_at: string;
  settled_at: string | null;
}

export interface ConsignmentItem {
  id: number;
  item_id: string;
  variant_id?: string;
  supplier_id: string;
  commission_rate: number;
  note: string | null;
  created_at: string;
  /** Giá ký gửi chụp lúc gán NCC — gốc chia tiền, không đổi khi sửa giá bán. */
  basis_price?: number | null;
  item_name?: string;
  sku?: string;
  sell_price?: number;
  unit_cost?: number;
  in_stock?: number;
  item_created_at?: string;
  import_date?: string | null;
  import_qty?: number | null;
  stock_updated_at?: string;
}

export interface ConsignmentReportRow {
  item_id: string;
  item_name: string;
  sku: string;
  commission_rate: number;
  qty_sold: number;
  revenue: number;
  commission: number;
  sell_price: number;
  unit_cost: number;
  supplier_payable: number;
  shop_profit: number;
}

export interface ConsignmentReport {
  rows: ConsignmentReportRow[];
  totalCommission: number;
  total_revenue: number;
  total_supplier_payable: number;
  total_shop_profit: number;
  from: string;
  to: string;
}

export interface ConsignmentOverviewSupplier {
  supplier_id: string;
  supplier_name: string;
  discount_rate: number;
  item_count: number;
  qty_sold: number;
  revenue: number;
  supplier_payable: number;
  shop_profit: number;
}

export interface ConsignmentOverview {
  from: string;
  to: string;
  totals: {
    item_count: number;
    qty_sold: number;
    revenue: number;
    supplier_payable: number;
    shop_profit: number;
    supplier_count: number;
    suppliers_with_sales: number;
  };
  suppliers: ConsignmentOverviewSupplier[];
}

/** Doanh thu theo ngày · từng NCC (chart overview). days[i] = { date, [supplier_id]: revenue } */
export interface ConsignmentOverviewSeries {
  from: string;
  to: string;
  suppliers: { supplier_id: string; supplier_name: string }[];
  days: Array<{ date: string } & Record<string, number | string>>;
}

export interface OpsTodo {
  id: string;
  title: string;
  description: string | null;
  /** category là TEXT tự do ở BE — schema comment liệt kê pack|ship|stock|call|other
   *  FE thêm "print" cho pending in tem (mục đích(display)của TodoWrite). */
  category: "pack" | "ship" | "stock" | "call" | "print" | "other" | (string & {});
  priority: "urgent" | "normal" | "low";
  due_date: string | null;
  status: "pending" | "in_progress" | "done";
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  /** JSON string {items:[{id,name,qty}]} — pending in tem (worker ghi vào meta, KHÔNG phải description). */
  meta?: string | null;
}

export interface BackfillEntry {
  id: string;
  item_name: string;
  sku: string | null;
  expected_qty: number;
  actual_qty: number;
  delta: number;
  reason: "damaged" | "lost" | "returned" | "counted_wrong" | "other";
  approver: string | null;
  status: "pending_approval" | "approved" | "rejected";
  note: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
}

export interface AssetEntry {
  id: string;
  name: string;
  category: "equipment" | "furniture" | "electronics" | "tool" | "other";
  purchase_date: string;
  purchase_price: number;
  depreciation_years: number;
  status: "active" | "broken" | "disposed";
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface BackupHistoryEntry {
  id: string;
  timestamp: string;
  target: "json" | "sheet";
  size_bytes: number;
  counts: Record<string, number>;
  status: "success" | "failed";
  error?: string;
}

// ═══════════════════════════════════════════════════
// Smart Stock Alerts
// ═══════════════════════════════════════════════════

export type StockAlertStatus = "urgent" | "warning" | "ok" | "slow";

export interface StockAlert {
  product_id: string;
  sku: string;
  name: string;
  category: string | null;
  current_stock: number;
  sold_30d: number;
  daily_velocity: number;
  days_remaining: number | null;
  status: StockAlertStatus;
  recommended_reorder: number;
}

export interface StockAlertsResponse {
  alerts: StockAlert[];
  summary: { urgent: number; warning: number; ok: number; slow: number };
}

// ═══════════════════════════════════════════════════
// ABC Analysis (Pareto)
// ═══════════════════════════════════════════════════

export type ABCClass = "A" | "B" | "C";

export interface ABCProduct {
  product_id: string;
  sku: string | null;
  name: string;
  category: string | null;
  qty_sold: number;
  revenue: number;
  revenue_pct: number;
  cumulative_pct: number;
  class: ABCClass;
}

export interface ABCResponse {
  products: ABCProduct[];
  summary: {
    total_revenue: number;
    count_a: number;
    count_b: number;
    count_c: number;
    revenue_a: number;
    revenue_b: number;
    revenue_c: number;
  };
  days: number;
}

// ═══════════════════════════════════════════════════
// Predictive Restock
// ═══════════════════════════════════════════════════

export type RestockStatus = "urgent" | "soon" | "ok" | "idle";

export interface RestockProduct {
  product_id: string;
  sku: string | null;
  name: string;
  current_stock: number;
  daily_velocity: number;
  lead_time_days: number;
  safety_stock_days: number;
  reorder_point: number;
  recommended_qty: number;
  status: RestockStatus;
  days_until_reorder: number | null;
}

export interface RestockResponse {
  products: RestockProduct[];
  summary: {
    urgent: number;
    soon: number;
    ok: number;
    idle: number;
    total_recommended_qty: number;
  };
  days: number;
}

export interface RestockConfig {
  product_id: string;
  lead_time_days: number;
  safety_stock_days: number;
  service_level: number;
  notes: string | null;
  updated_at: string;
}

// ═══════════════════════════════════════════════════
// Suppliers & Ratings
// ═══════════════════════════════════════════════════

export interface Supplier {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  tax_id?: string | null;
  bank_account?: string | null;
  notes?: string | null;
  discount_rate?: number;
  rating_count: number;
  avg_on_time: number | null;
  avg_quality: number | null;
  avg_price: number | null;
  rating_score: number;
  created_at?: string;
  updated_at?: string;
}

export interface SupplierOrder {
  id: number;
  order_date: string | null;
  status: string;
  goods_type?: string | null;
  supplier_name: string | null;
  supplier_tax_id?: string | null;
  invoice_no?: string | null;
  supplier_id?: string | null;
  total_amount: number;
  missing_fields?: string[];
}

export interface SupplierRating {
  id: string;
  supplier_id: string;
  import_order_id?: string | null;
  on_time_score: number;
  quality_score: number;
  price_score: number;
  comment?: string | null;
  created_at: string;
}

// ═══════════════════════════════════════════════════
// AI Config
// ═══════════════════════════════════════════════════

export interface AIConfig {
  id: string;
  provider: string;
  base_url?: string | null;
  model?: string | null;
  is_active: number;
  feature: string;
  api_key_status?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface AIConfigMultiProvider {
  api_key_status: "set" | "";
  api_keys_status: Array<"set" | "">;
  base_url: string;
}

export interface AIConfigMulti {
  active: string;
  model: string;
  lead_provider?: string;
  lead_model?: string;
  culy_provider?: string;
  culy_model?: string;
  vision_provider?: string;
  vision_model?: string;
  providers: Record<string, AIConfigMultiProvider>;
  max_tokens: number;
  updated_at: string;
}

export interface ProductImage {
  id: string;
  telegram_file_id: string;
  uploaded_at: string;
}

export interface SupplierAiConfig {
  provider: string | null;
  model: string | null;
  updated_at: string | null;
}

export interface DataMaturity {
  days_with_receipts: number;
  total_receipts: number;
  first_receipt_at?: string;
  last_receipt_at?: string;
}

// ═══════════════════════════════════════════════════
// Consignment Category Mappings
// ═══════════════════════════════════════════════════

export interface CategoryMappingEntry {
  id: number;
  supplier_id: string;
  category_id: string;
  category_name: string;
  commission_rate: number;
  note: string | null;
  created_at: string;
}

export interface ConsignmentCategoryReportRow {
  item_name: string;
  category_name: string;
  qty_sold: number;
  revenue: number;
  commission_rate: number;
  shop_share: number;
  supplier_share: number;
}

export interface ConsignmentCategoryReport {
  rows: ConsignmentCategoryReportRow[];
  total_revenue: number;
  total_supplier_share: number;
  total_shop_share: number;
}

// ═══════════════════════════════════════════════════
// Web POS
// ═══════════════════════════════════════════════════

export type PosPaymentMethod = "CASH" | "TRANSFER";

export type PosPrintStatus = "sent" | "received" | "printing" | "succeeded" | "failed_pre_send" | "unknown";

export interface PosCatalogParams {
  limit?: number;
  offset?: number;
  updated_since?: string;
}

export interface PosCatalogProduct {
  variant_id: string;
  item_id: string;
  name: string;
  category_id: string | null;
  category_name: string | null;
  barcode: string | null;
  sku: string | null;
  /** Charged unit price (sale price when is_sale). */
  price_vnd: number;
  cost_vnd: number;
  track_stock: number;
  stock: number;
  image_url: string | null;
  list_price_vnd: number | null;
  is_sale: number;
  sale_ends_at: string | null;
  updated_at: string | null;
}

export interface PosCatalogResponse {
  products: PosCatalogProduct[];
  limit: number;
  offset: number;
}

export interface PosBarcodeResponse {
  product: PosCatalogProduct | null;
}

/** Browser web-sales: NO unit price / item name / item ID — price resolved server-side. */
export interface WebPosSaleRequest {
  client_sale_id: string;
  device_id: string;
  sold_at: string;
  payment_method: PosPaymentMethod;
  tendered_vnd: number;
  change_vnd: number;
  discount_vnd: number;
  cart_rev?: number;
  cashier?: string | null;
  lines: { variant_id: string; qty: number }[];
}

export interface PosCartLine {
  variant_id: string;
  qty: number;
}

export interface PosCartTab {
  id: string;
  label: string;
  lines: PosCartLine[];
  discount_vnd: number;
  note: string;
}

export interface PosCartState {
  carts: PosCartTab[];
  active_index: number;
}

export interface PosGetCartResponse {
  rev: number;
  state: PosCartState;
  locked_by: string | null;
  locked_at: string | null;
}

export interface PosPutCartRequest {
  expected_rev: number;
  state: PosCartState;
}

export type PosPutCartResponse =
  | { ok: true; rev: number; state: PosCartState }
  | { error: "CART_CHANGED" | "CART_LOCKED"; rev: number; state: PosCartState }
  | { error: "BAD_CART_STATE" | "CART_VERSION"; reason?: string };

export interface PosCheckoutConflict {
  error: "CART_CHANGED" | "CART_LOCKED";
  rev: number;
  state: PosCartState;
}

export interface PosWsTicketResponse {
  ticket: string;
  expires_in: number;
}

export interface PosSaleLine {
  variant_id: string;
  item_id: string;
  item_name: string;
  sku: string | null;
  qty: number;
  unit_price_vnd: number;
  list_price_vnd: number | null;
  /** Backend real field — bill line total (qty × unit_price_vnd). */
  line_total_vnd: number;
  /** Original sale only: cumulative qty returned by COMPLETED refunds. Absent on older backend. */
  returned_qty?: number;
  /** Original sale only: qty − returned_qty. Absent on older backend → falls back to qty. */
  remaining_qty?: number;
}

export interface PosSale {
  client_sale_id: string;
  receipt_number: string | null;
  device_id: string;
  sold_at: string;
  payment_method: PosPaymentMethod;
  tendered_vnd: number;
  change_vnd: number;
  discount_vnd: number;
  total_vnd: number;
  status: string;
  original_client_sale_id: string | null;
  cashier: string | null;
  lv_receipt_number: string | null;
  lines?: PosSaleLine[];
}

export interface PosSaleResponse {
  client_sale_id: string;
  receipt_number: string | null;
  total_vnd: number;
  change_vnd: number;
  payment_method: PosPaymentMethod;
  idempotent: boolean;
  lv_receipt_number: string | null;
}

export interface PosSaleDetailResponse {
  sale: PosSale;
  lines: PosSaleLine[];
}

export interface PosSalesParams {
  from?: string;
  to?: string;
  device_id?: string;
  include_lines?: boolean;
  limit?: number;
}

export interface PosSalesResponse {
  sales: PosSale[];
  limit: number;
}

export interface PosVoidRequest {
  reason: string;
}

export interface PosVoidResponse {
  ok: boolean;
  client_sale_id: string;
  idempotent: boolean;
  voided_at: string;
}

export interface PosReturnRequest {
  client_sale_id: string;
  device_id: string;
  refund_method: PosPaymentMethod;
  reason: string;
  lines: { variant_id: string; qty: number }[];
}

export interface PosReturnResponse {
  ok: boolean;
  client_sale_id: string;
  original_client_sale_id: string;
  receipt_number: string | null;
  total_vnd: number;
  idempotent: boolean;
}

/** Drawer balance is store-wide (server-authoritative); no device/at params. */
export interface PosDrawerBalanceParams {}

export interface PosDrawerBalanceResponse {
  balance_vnd: number;
  /** null when no (non-voided) events exist. */
  as_of: string | null;
}

// ── PIN ca hàng / cửa sổ mở khoá (SEAM) ────────────
// Deterrent only — never word copy as a security guarantee.
export interface PosPinSettings {
  /** Đã có PIN đặt trên server chưa. KHÔNG trả PIN/hash về client. */
  pin_set: boolean;
  /** Thời lượng cửa sổ mở khoá sau khi nhập đúng PIN (phút). Mặc định 5. */
  unlock_window_minutes: number;
}

// ─ Lịch sử sửa hàng hoá (items_audit_log) ────────────
export interface ItemAuditLogQuery {
  /** Lọc theo tên / SKU / item_id. */
  q?: string;
  /** ISO hoặc yyyy-mm-dd (>= changed_at). */
  from?: string;
  /** ISO hoặc yyyy-mm-dd (<= changed_at). */
  to?: string;
  /** create | update | delete | adjust_stock | bulk_update | consume_material | push_loyverse ... */
  action?: string;
  limit?: number;
}

export interface ItemAuditEntry {
  id: number;
  item_id: string;
  item_name: string | null;
  sku: string | null;
  action: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  changed_at: string;
  source: string | null;
}

export interface ItemAuditLogResponse {
  entries: ItemAuditEntry[];
  /** Dùng cho dropdown action filter — worker tổng hợp DISTINCT action. */
  actions: string[];
  total: number;
}

export interface PosPrintRequest {
  event_id: string;
  target_device_id: string;
}

export interface PosPrintRequestResponse {
  ok: boolean;
  event_id?: string;
  client_sale_id?: string;
  target_device_id?: string;
  attempt?: number;
  status?: PosPrintStatus;
  code?: string;
  retryable?: boolean;
  ack_count?: number;
  updated_at?: string;
  error?: string;
  duplicate?: boolean;
}

// ─ Lịch sử in tem (print_history) ────────────────
/** Nguồn phát lệnh in. Contract CỐ ĐỊNH với worker — không đổi tên field. */
export type PrintHistorySource = "barcode-page" | "products-page" | "consignment" | "import-order";

export interface PrintHistoryItem {
  item_id: string;
  item_name: string;
  sku: string;
  barcode: string;
  qty: number;
}

export interface PrintHistoryCreateRequest {
  items: PrintHistoryItem[];
  /** Một trong PrintHistorySource (worker có thể nhận giá trị khác). */
  source: string;
}

export interface PrintHistoryCreateResponse {
  ok: true;
  id: number;
}

export interface PrintHistoryEntry {
  id: number;
  created_at: string;
  source: string;
  created_by: string | null;
  item_count: number;
  items: PrintHistoryItem[];
}

export interface PrintHistoryResponse {
  entries: PrintHistoryEntry[];
  total: number;
}

export interface PrintHistoryQuery {
  limit?: number;
  q?: string;
}

// ─ Xoá lịch sử in tem ────────────────────────────
/** Trả về của `DELETE /api/print-history/:id`. */
export interface PrintHistoryDeleteResponse {
  ok: true;
  id: number;
  deleted: number;
}

/** Body bắt buộc của `POST /api/print-history/clear-all` — chốt an toàn chống xoá nhầm. */
export interface PrintHistoryClearAllRequest {
  all: true;
}

export interface PrintHistoryClearAllResponse {
  ok: true;
  deleted: number;
}

/** Body bắt buộc của `POST /api/print-history/delete` — ids phải là mảng số, không rỗng. */
export interface PrintHistoryDeleteManyRequest {
  ids: number[];
}

export interface PrintHistoryDeleteManyResponse {
  ok: true;
  deleted: number;
}

export {};
